import React, { useContext, useDeferredValue, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { apiFetch, useAuth } from '../../context/AuthContext';
import { useProfile } from '../../hooks/useProfile';
import {
  AlertCircle, Search, Users, X, Plus, Menu, User, Shield, ChevronLeft, ChevronRight,
  MoreVertical, MessageSquare, Star, Pencil, Check, Heart, BellOff, Ban, ListPlus, FolderClosed, Archive, GripVertical,
  Settings, UserPlus, Camera,
} from 'lucide-react';
import { ThemeContext } from '../../constant/theme';
import { LanguageContext } from '../../constant/LanguageContext';
import { SidebarContext, useSidebar } from '../(All) Sidebar/SidebarContext';
import { useMessenger } from '../../context/MessengerContext';
import ChatHeader from './components/ChatHeader';
import ChatInput from './components/ChatInput';
import ConversationList from './components/ConversationList';
import EmployeeList from './components/EmployeeList';
import EmptyState from './components/EmptyState';
import MessagesContainer from './components/MessagesContainer';
import StarredMessagesModal from './components/StarredMessagesModal';
import ForwardModal from './components/ForwardModal';
import MediaLinksDocsModal from './components/MediaLinksDocsModal';
import ChatSearchBar from './components/ChatSearchBar';
import MediaViewerModal from './components/MediaViewerModal';
import useMediaUpload from './hooks/useMediaUpload';
import useMessages from './hooks/useMessages';
import useMessengerInit from './hooks/useMessengerInit';
import useProfilePics from './hooks/useProfilePics';
import useReadReceipts from './hooks/useReadReceipts';
import useSocket from './hooks/useSocket';
import useVoiceRecorder from './hooks/useVoiceRecorder';
import AlertModal from '../../components/AlertModal';
import ReusableDropdown from '../../components/ReusableDropdown';
import { translations } from './constants/translations';
import { getColors } from './style/messengerColors';
import { getAvatarColor, getTrilateralName, formatMessageTime } from './utils/formatters';
import { isSameUser } from './utils/userHelpers';
import { buildSystemMessageText } from './utils/systemMessages';
import { getResponsiveStyles } from './style/Message_Style';
import { SkeletonStyles, SkeletonBox, LoadingPill } from '../../components/SkeletonLoader';
const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

const MessengerPage = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { isCollapsed } = useSidebar();
  const { profile } = useProfile();
  const { hasFeatureAccess, getPageState } = useAuth();
  const isRTL = language === 'ar';

  // ── Admin-level feature gating (registry page "messages") ────────────────
  // Only applies to promoted users browsing in Admin View; normal user /
  // manager views keep full messenger behavior. hasFeatureAccess bypasses
  // for admin/superadmin roles.
  const messengerViewType =
    localStorage.getItem('managerViewType') ||
    localStorage.getItem('viewType') ||
    'user';
  const isAdminViewGated = messengerViewType === 'admin';
  // Page-level check, not hasFeatureAccess('messages', 'view') — the
  // "messages" page in the permission registry has no "view" feature key at
  // all (only send/remove/reply/react/forward/...), so there's no control in
  // Admin Level Configuration that could ever grant it. That made this
  // resolve to false for every promoted user regardless of Full Access,
  // showing Access Denied unconditionally (same bug as Employee Management's
  // canViewEmployees). PageGuard on the /messages route already enforces
  // page-level HIDDEN/VISIBLE_ACCESS_DENIED before this component renders —
  // this mirrors that same check instead of a nonexistent feature grant.
  const canViewMessages = !isAdminViewGated || getPageState('messages') === 'VISIBLE_ACCESSIBLE';
  const canSendMessages = !isAdminViewGated || hasFeatureAccess('messages', 'send');
  const canDeleteMessages = !isAdminViewGated || hasFeatureAccess('messages', 'remove');
  const canReplyMessages = !isAdminViewGated || hasFeatureAccess('messages', 'reply');
  const canReactMessages = !isAdminViewGated || hasFeatureAccess('messages', 'react');
  const canForwardMessages = !isAdminViewGated || hasFeatureAccess('messages', 'forward');
  const canViewMessageDetails = !isAdminViewGated || hasFeatureAccess('messages', 'viewDetails');
  const canCreateGroup = !isAdminViewGated || hasFeatureAccess('messages', 'createGroup');
  // Messenger Phase 1: creating/reconfiguring a DEPARTMENT_SYNC ("org-synced")
  // group is Admin-only — plain role check, not hasFeatureAccess/the
  // `!isAdminViewGated ||` pattern used above: those bypass unconditionally
  // for a manager/user in their own (non-Admin-View) session, which would let
  // any manager create a synced group regardless of grants.
  const canCreateOrgGroup = profile?.role === 'admin' || profile?.role === 'superadmin';
  const canStarMessages = !isAdminViewGated || hasFeatureAccess('messages', 'starredMessages');
  const canBlockUsers = !isAdminViewGated || hasFeatureAccess('messages', 'block');

  const {
    conversations,
    setConversations,
    messages,
    setMessages,
    onlineUsers,
    setOnlineUsers,
    currentUser,
    setCurrentUser,
    socket: contextSocket,
    setSocket,
    isConnected: contextIsConnected,
    setIsConnected,
    employeeProfilePics,
    setEmployeeProfilePics,
    selectedUser: contextSelectedUser,
    setSelectedUser: setContextSelectedUser,
    setIsMessengerPageOpen,
    chatPrefs,
  } = useMessenger();

  // Tell the Mini Chat Dock (always mounted, everywhere) that the full inbox
  // is open right now so it can hide itself instead of showing a redundant
  // second chat surface at the same time.
  useEffect(() => {
    setIsMessengerPageOpen(true);
    return () => setIsMessengerPageOpen(false);
  }, [setIsMessengerPageOpen]);

  const fileInputRef = useRef(null);
  const rightPanelRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [showRightPanel, setShowRightPanel] = useState(false);
  // 'all' is the default landing filter.
  const [activeTab, setActiveTab] = useState('all');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatModalTab, setNewChatModalTab] = useState('direct');
  const [newChatSearch, setNewChatSearch] = useState('');
  const [newGroupSearch, setNewGroupSearch] = useState('');
  // Filtering + rendering these lists against the full employee directory
  // (1000s of rows, each with avatar/name/etc.) is expensive enough that
  // doing it synchronously on every keystroke made typing feel frozen for
  // a second or more per character. useDeferredValue keeps the input itself
  // instantly responsive and lets React deprioritize (and interrupt, if
  // more typing comes in) the expensive list re-render.
  const deferredNewChatSearch = useDeferredValue(newChatSearch);
  const deferredNewGroupSearch = useDeferredValue(newGroupSearch);
  const MAX_RENDERED_RESULTS = 50;
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  // Messenger Phase 1 (Synced & Community Groups) — group-creation settings.
  const [groupMembershipMode, setGroupMembershipMode] = useState('MANUAL');
  const [groupPostingMode, setGroupPostingMode] = useState('EVERYONE');
  const [groupSyncDepartmentId, setGroupSyncDepartmentId] = useState(null);
  const [groupSyncWorkingCompany, setGroupSyncWorkingCompany] = useState(null);
  const [groupAutoAddNewMembers, setGroupAutoAddNewMembers] = useState(true);
  const [syncDepartments, setSyncDepartments] = useState([]);
  const [syncDepartmentsLoading, setSyncDepartmentsLoading] = useState(false);
  const [syncCompanies, setSyncCompanies] = useState([]);
  const [syncCompaniesLoading, setSyncCompaniesLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const groupPhotoInputRef = useRef(null);

  const handleGroupPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !initState.selectedUser || !isGroupConv(initState.selectedUser)) return;
    
    const actualGroupId = String(initState.selectedUser.id).replace('group-', '');
    const formData = new FormData();
    formData.append('photo', file);
    
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/photo`, {
        method: 'POST',
        body: formData,
      });
      if (res.success) {
        const localUrl = URL.createObjectURL(file);
        setEmployeeProfilePics(prev => ({ ...prev, [initState.selectedUser.id]: localUrl }));
      }
    } catch (err) {
      setAlertState({
        open: true,
        title: isRTL ? 'خطأ' : 'Error',
        message: isRTL ? 'فشل تحميل الصورة' : 'Failed to upload photo',
        variant: 'error'
      });
    }
    e.target.value = '';
  };

  const [editGroupSearch, setEditGroupSearch] = useState('');
  // Messenger Phase 1 — Group Settings modal (postingMode / membershipMode /
  // sync source + sync-status readout for DEPARTMENT_SYNC groups).
  const [showGroupSettingsModal, setShowGroupSettingsModal] = useState(false);
  const [groupSettingsTarget, setGroupSettingsTarget] = useState(null);
  const [settingsPostingMode, setSettingsPostingMode] = useState('EVERYONE');
  const [settingsMembershipMode, setSettingsMembershipMode] = useState('MANUAL');
  const [settingsSyncDepartmentId, setSettingsSyncDepartmentId] = useState(null);
  const [settingsSyncWorkingCompany, setSettingsSyncWorkingCompany] = useState(null);
  const [settingsAutoAddNewMembers, setSettingsAutoAddNewMembers] = useState(true);
  const [settingsSyncDepartments, setSettingsSyncDepartments] = useState([]);
  const [settingsSyncDepartmentsLoading, setSettingsSyncDepartmentsLoading] = useState(false);
  const [settingsSyncCompanies, setSettingsSyncCompanies] = useState([]);
  const [settingsSyncCompaniesLoading, setSettingsSyncCompaniesLoading] = useState(false);
  const [groupSyncStatus, setGroupSyncStatus] = useState(null);
  const [groupSyncStatusLoading, setGroupSyncStatusLoading] = useState(false);
  const [savingGroupSettings, setSavingGroupSettings] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(
    () => parseInt(localStorage.getItem('messengerSidebarWidth') || '380', 10)
  );
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(380);
  const [alertState, setAlertState] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info',
    showCancel: false,
    onConfirm: null
  });
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
  });
  // ── WhatsApp-style feature state ─────────────────────────────────────────
  const [showSidebarMenu, setShowSidebarMenu] = useState(false);
  const [showStarredModal, setShowStarredModal] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);       // message being replied to
  const [forwardMsg, setForwardMsg] = useState(null);       // message being forwarded
  const [showMediaDocs, setShowMediaDocs] = useState(false);
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [chatSearchIndex, setChatSearchIndex] = useState(0);
  const [jumpToMessageId, setJumpToMessageId] = useState(null);
  const [viewerState, setViewerState] = useState(null);     // { message } — unified lightbox
  const [aliasEditing, setAliasEditing] = useState(false);
  const [aliasValue, setAliasValue] = useState('');
  const [listInputOpen, setListInputOpen] = useState(false);
  const [listNameValue, setListNameValue] = useState('');
  const sidebarMenuRef = useRef(null);

  const t = translations[language];
  const c = useMemo(() => getColors(theme), [theme]);
  const { isMobile } = screenSize;

  const socketState = useSocket();

  // Per-user device-local prefs (starred / favorites / pinned / aliases /
  // muted / blocked / custom lists) now come from MessengerContext, shared
  // with the Mini Chat Dock, so starring/pinning here or there stays in sync.

  const messagesState = useMessages({
    socket: socketState.socket || contextSocket,
    isConnected: socketState.isConnected || contextIsConnected,
    currentUser,
    selectedUser: contextSelectedUser,
    messages,
    setMessages,
    conversations,
    setConversations,
    allEmployees: [],
    userNamesCache: {},
    onlineUsers,
    messageInput,
    setMessageInput,
    t,
    setAlertState,
    replyingTo,
    clearReply: () => setReplyingTo(null),
  });

  const initState = useMessengerInit({
    currentUser,
    setCurrentUser,
    conversations,
    setConversations,
    messages,
    setMessages,
    onlineUsers,
    employeeProfilePics,
    fetchEmployeeProfilePic: async () => null,
    fetchMessagesForUser: messagesState.fetchMessagesForUser,
    isSameUser,
    language,
    t,
  });

  const profilePicsState = useProfilePics({
    token: initState.token,
    conversations,
    employeeProfilePics,
    setEmployeeProfilePics,
  });

  const voiceState = useVoiceRecorder(t);

  const mediaState = useMediaUpload({
    selectedUser: initState.selectedUser,
    isConnected: socketState.isConnected || contextIsConnected,
    messageInput,
    setMessageInput,
    setMessages,
    setConversations,
    currentUser,
    onlineUsers,
    socket: socketState.socket || contextSocket,
    t,
  });

  useEffect(() => {
    if (socketState.socket) setSocket(socketState.socket);
    setIsConnected(socketState.isConnected);
  }, [socketState.socket, socketState.isConnected, setSocket, setIsConnected]);

  const handleGroupJoined = useCallback((groupData, user) => {
    setConversations(prev => {
      const groupId = `group-${groupData.id}`;
      // Skip if group already in list (handles race condition between API response and socket event)
      if (prev.some(c => c.id === groupId)) return prev;

      // If the current user created this group, handleCreateGroup already added it via the API response.
      // The socket 'chat_group:joined' also fires for the creator — skip to avoid duplicates.
      const myCode = String(currentUser?.infinityCode || '');
      const creatorCode = String(groupData.createdBy || groupData.addedByCode || '');
      if (myCode && creatorCode && myCode === creatorCode) return prev;

      const newGroup = {
        id: groupId,
        name: groupData.name,
        avatar: groupData.name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
        online: false,
        isGroup: true,
        createdBy: groupData.createdBy,
        members: groupData.members?.map(m => ({
          id: m.infinityCode,
          name: m.employee?.sr || m.employee?.name || `User ${m.infinityCode}`,
          role: m.role
        })) || [],
        lastMessage: '',
        time: t.justNow,
        timestamp: Date.now(),
        unread: 0,
      };

      return [newGroup, ...prev];
    });
  }, [t.justNow, isRTL, setConversations, currentUser]);

  const handleGroupMembersAdded = useCallback((data) => {
    const { groupId, newMembers } = data;
    const fullGroupId = groupId.startsWith('group-') ? groupId : `group-${groupId}`;

    const resolvedMembers = newMembers.map(code => {
      const emp = initState.allEmployees.find(e => isSameUser(e.id, code));
      return {
        id: code,
        name: emp?.name || `User ${code}`,
        role: 'member'
      };
    });

    setConversations(prev => prev.map(conv => {
      if (conv.id === fullGroupId) {
        const existing = conv.members || [];
        const filteredExisting = existing.filter(m => !newMembers.includes(m.id));
        return {
          ...conv,
          members: [...filteredExisting, ...resolvedMembers]
        };
      }
      return conv;
    }));

    if (initState.selectedUser?.id === fullGroupId) {
      initState.setSelectedUser(prev => {
        const existing = prev.members || [];
        const filteredExisting = existing.filter(m => !newMembers.includes(m.id));
        return {
          ...prev,
          members: [...filteredExisting, ...resolvedMembers]
        };
      });
    }
  }, [initState.allEmployees, initState.selectedUser, setConversations]);

  const handleGroupMemberRemoved = useCallback((data) => {
    const { groupId, removedCode } = data;
    const fullGroupId = groupId.startsWith('group-') ? groupId : `group-${groupId}`;

    setConversations(prev => prev.map(conv => {
      if (conv.id === fullGroupId) {
        return {
          ...conv,
          members: (conv.members || []).filter(m => !isSameUser(m.id, removedCode))
        };
      }
      return conv;
    }));

    if (initState.selectedUser?.id === fullGroupId) {
      initState.setSelectedUser(prev => ({
        ...prev,
        members: (prev.members || []).filter(m => !isSameUser(m.id, removedCode))
      }));
    }
  }, [initState.selectedUser, setConversations]);

  const handleGroupRemoved = useCallback((data) => {
    const { groupId } = data;
    const fullGroupId = groupId.startsWith('group-') ? groupId : `group-${groupId}`;

    setConversations(prev => prev.map(conv => {
      if (conv.id === fullGroupId) {
        return { ...conv, isArchived: true };
      }
      return conv;
    }));

    if (initState.selectedUser?.id === fullGroupId) {
      initState.setSelectedUser(prev => ({ ...prev, isArchived: true }));
    }
  }, [initState.selectedUser, setConversations]);

  // Phase 3: Handle group message status updates (delivered/read aggregate)
  const handleGroupStatusUpdate = useCallback((data) => {
    const { groupId, messages: statusUpdates } = data;
    if (!statusUpdates || !Array.isArray(statusUpdates)) return;
    const fullGroupId = groupId?.startsWith('group-') ? groupId : `group-${groupId}`;

    setMessages((prev) => {
      const list = prev[fullGroupId];
      if (!list) return prev;

      let changed = false;
      const updated = list.map((msg) => {
        const match = statusUpdates.find((u) => u.messageId === msg.id);
        if (match && msg.sent && msg.status !== match.status) {
          changed = true;
          return { ...msg, status: match.status };
        }
        return msg;
      });

      return changed ? { ...prev, [fullGroupId]: updated } : prev;
    });
  }, [setMessages]);

  // Handle real-time group message deletion events
  const handleGroupMessageDeleted = useCallback((data) => {
    const { messageId, groupId } = data;
    const fullGroupId = groupId?.startsWith('group-') ? groupId : `group-${groupId}`;

    setMessages((prev) => {
      const list = prev[fullGroupId];
      if (!list) return prev;
      const filtered = list.filter((m) => m.id !== parseInt(messageId, 10) && m.id !== messageId);
      if (filtered.length === list.length) return prev;
      return { ...prev, [fullGroupId]: filtered };
    });

    // Update conversation preview
    setConversations((prev) => prev.map((conv) => {
      if (conv.id !== fullGroupId) return conv;
      return { ...conv }; // Trigger re-render; lastMessage will be stale but acceptable
    }));
  }, [setMessages, setConversations]);

  const handleSystemMessage = useCallback((data) => {
    const rawGroupId = String(data.groupId ?? '');
    const fullGroupId = rawGroupId.startsWith('group-') ? rawGroupId : `group-${rawGroupId}`;
    const payload = data.payload || {};
    const systemMessage = {
      id: data.id ? `sys-${data.id}` : `sys-${Date.now()}`,
      isSystem: true,
      type: data.type,
      text: buildSystemMessageText(data.type, payload, t),
      time: formatMessageTime(data.created_at || data.timestamp || new Date().toISOString()),
      sent: false,
      status: 'read',
      raw: { created_at: data.created_at || data.timestamp || new Date().toISOString(), groupId: rawGroupId },
    };

    setMessages((prev) => {
      const list = prev[fullGroupId] || [];
      if (list.some((m) => String(m.id) === String(systemMessage.id))) return prev;
      return { ...prev, [fullGroupId]: [...list, systemMessage] };
    });
  }, [setMessages, t]);

  // Live-patch a group's postingMode/membershipMode/sync fields when an admin
  // changes them elsewhere — keeps composer gating (ADMINS_ONLY) and any open
  // Group Settings modal in sync without a refetch.
  const handleGroupSettingsChanged = useCallback((data) => {
    const rawGroupId = String(data.groupId ?? '');
    const fullGroupId = rawGroupId.startsWith('group-') ? rawGroupId : `group-${rawGroupId}`;
    const patch = {
      ...(data.membershipMode !== undefined && { membershipMode: data.membershipMode }),
      ...(data.postingMode !== undefined && { postingMode: data.postingMode }),
      ...(data.syncDepartmentId !== undefined && { syncDepartmentId: data.syncDepartmentId }),
      ...(data.syncWorkingCompany !== undefined && { syncWorkingCompany: data.syncWorkingCompany }),
      ...(data.autoAddNewMembers !== undefined && { autoAddNewMembers: data.autoAddNewMembers }),
    };
    if (Object.keys(patch).length === 0) return;

    setConversations((prev) => prev.map((conv) => (conv.id === fullGroupId ? { ...conv, ...patch } : conv)));

    if (initState.selectedUser?.id === fullGroupId) {
      initState.setSelectedUser((prev) => ({ ...prev, ...patch }));
    }
  }, [initState.selectedUser, setConversations]);


  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    initState.initializeMessaging((authToken, user) =>
      socketState.initializeSocket(authToken, user, {
        handleIncomingMessage: messagesState.handleIncomingMessage,
        handleGroupJoined,
        handleGroupMembersAdded,
        handleGroupMemberRemoved,
        handleGroupRemoved,
        handleGroupStatusUpdate,
        handleGroupMessageDeleted,
        handleSystemMessage,
        handleGroupSettingsChanged,
        handleReactionEvent: messagesState.handleReactionEvent,
        setTypingUsers,
        markMessagesAsRead: messagesState.markMessagesAsRead,
        setOnlineUsers,
        setIsConnected,
        onError: initState.setError,
      })
    );

    // Use the ref-backed disconnect so we always tear down the live socket,
    // not the null captured in this closure on first render. Without this
    // the messenger socket leaks every time the user navigates away —
    // server keeps it as "alive" until ping-timeout, presence counter never
    // drops to zero, and `user:offline` never fires for that user.
    return () => {
      socketState.disconnectSocket?.();
    };
  }, []);

  // Keep the socket's handlers ref pointed at the latest closures every
  // render. `initializeSocket` captures handlers ONCE on mount, so without
  // this push the listeners would invoke the very first `handleIncomingMessage`
  // (which closed over `selectedUser = null` etc.) on every event — the
  // open-chat read-receipt emit then never fires because the captured
  // selectedUser doesn't match the chat partner.
  useEffect(() => {
    socketState.updateHandlers?.(
      {
        handleIncomingMessage: messagesState.handleIncomingMessage,
        handleGroupJoined,
        handleGroupMembersAdded,
        handleGroupMemberRemoved,
        handleGroupRemoved,
        handleGroupStatusUpdate,
        handleGroupMessageDeleted,
        handleSystemMessage,
        handleGroupSettingsChanged,
        handleReactionEvent: messagesState.handleReactionEvent,
        setTypingUsers,
        markMessagesAsRead: messagesState.markMessagesAsRead,
        setOnlineUsers,
        setIsConnected,
        onError: initState.setError,
      },
      currentUser,
    );
  }, [
    socketState,
    messagesState.handleIncomingMessage,
    handleGroupJoined,
    handleGroupMembersAdded,
    handleGroupMemberRemoved,
    handleGroupRemoved,
    handleGroupStatusUpdate,
    handleGroupMessageDeleted,
    handleSystemMessage,
    handleGroupSettingsChanged,
    messagesState.markMessagesAsRead,
    currentUser,
    initState.setError,
  ]);

  useEffect(() => {
    if (profilePicsState.fetchEmployeeProfilePic) {
      initState.setSelectedUser((prev) => prev);
    }
  }, [profilePicsState.fetchEmployeeProfilePic]);

  useEffect(() => {
    if (contextSelectedUser?.id !== initState.selectedUser?.id) {
      setContextSelectedUser(initState.selectedUser);
    }
  }, [initState.selectedUser]);

  // ── Read receipts for the open thread ───────────────────────────────────────
  // Sent on open, on every new incoming message, when the tab becomes
  // visible again and after a reconnect (which also resyncs the thread).
  // See hooks/useReadReceipts.js.
  const openChatId = initState.selectedUser?.id;
  useReadReceipts({
    socket: socketState.socket || contextSocket,
    chatId: openChatId,
    messages: openChatId ? messages[openChatId] : undefined,
    isActive: !!openChatId,
    setConversations,
    onResync: messagesState.resyncMessagesForUser,
  });

  useEffect(() => {
    if (currentUser?.infinityCode && contextSelectedUser && !initState.selectedUser) {
      // Only restore selectedUser if user explicitly selected it before (e.g. navigating back)
      // Do NOT auto-select first chat on initial load
      if (contextSelectedUser._userSelected) {
        initState.setSelectedUser(contextSelectedUser);
      }
    }
  }, [currentUser?.infinityCode]);

  useEffect(() => {
    if (!currentUser?.infinityCode) return;

    const foundSelf = initState.allEmployees.find((emp) => isSameUser(emp.id, currentUser.infinityCode));
    if (foundSelf) {
      initState.setAllEmployees((prev) => prev.filter((emp) => !isSameUser(emp.id, currentUser.infinityCode)));
    }

    const selfConversation = conversations.find((conv) => isSameUser(conv.id, currentUser.infinityCode));
    if (selfConversation) {
      setConversations((prev) => prev.filter((conv) => !isSameUser(conv.id, currentUser.infinityCode)));
    }
  }, [currentUser, initState.allEmployees, conversations]);

  useEffect(() => {
    if (!initState.selectedUser) return;
    if (messagesState.suppressAutoScrollRef.current) return;

    if (messagesState.forceScrollOnSelectRef.current) {
      messagesState.messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      messagesState.forceScrollOnSelectRef.current = false;
      return;
    }

    if (messagesState.userAtBottomRef.current) {
      messagesState.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, initState.selectedUser]);

  // Always land on the latest message when a chat is opened — including
  // re-opening a chat whose messages are already cached (no fetch runs, so
  // forceScrollOnSelectRef never gets set on that path).
  useEffect(() => {
    if (!initState.selectedUser) return;
    messagesState.userAtBottomRef.current = true;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesState.messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [initState.selectedUser?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRightPanel && rightPanelRef.current && !rightPanelRef.current.contains(event.target)) {
        if (!event.target.closest('.chat-info-btn')) {
          setShowRightPanel(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRightPanel]);

  const sendRecording = async () => {
    if (!voiceState.audioBlob || !initState.selectedUser) return;

    const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

    try {
      const voiceFile = new File([voiceState.audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', voiceFile);

      const uploadRes = await apiFetch(`${API_BASE_URL}/api/private-messages/upload/audio`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.error || 'Voice upload failed');
      }

      const { data: uploaded } = await uploadRes.json();

      const mediaData = {
        storageKey: uploaded.storageKey,
        url: uploaded.url,
        type: 'voice',
        name: uploaded.name || voiceFile.name,
        size: uploaded.size || voiceState.audioBlob.size,
        duration: voiceState.recordingTime,
        mimeType: 'audio/webm',
      };

      await mediaState.sendMediaMessage(mediaData, 'voice');
      voiceState.setAudioBlob(null);
    } catch (err) {
      console.error('Failed to send voice:', err);
      setAlertState({ open: true, title: t.error, message: t.sendFailed, variant: 'error' });
    }
  };

  const isGroupConv = (conv) => conv?.isGroup || (typeof conv?.id === 'string' && conv.id.startsWith('group-'));

  // Whether the current user is an admin of the currently-open group —
  // computed once here instead of the ad hoc `.some(...)` scans this used to
  // be duplicated across the Group Info panel and the composer gate.
  const isCurrentUserAdminOfSelectedGroup = useMemo(() => {
    if (!isGroupConv(initState.selectedUser)) return false;
    return !!initState.selectedUser?.members?.some(
      (m) => isSameUser(m.id, currentUser?.infinityCode) && m.role === 'admin'
    );
  }, [initState.selectedUser, currentUser?.infinityCode]);

  const filteredConversations = conversations.filter((conv) => {
    if (activeTab !== 'archived' && conv.isArchived) return false;
    if (activeTab === 'archived' && !conv.isArchived) return false;
    if (activeTab === 'groups' && !isGroupConv(conv)) return false;
    if (activeTab === 'messages' && isGroupConv(conv)) return false;
    if (activeTab === 'unread' && !(conv.unread > 0)) return false;
    if (activeTab === 'favorites' && !chatPrefs.isFavorite(conv.id)) return false;

    if (isGroupConv(conv) && !conv.isArchived) {
      const currentClientName = profile?.clientName || localStorage.getItem('clientName') || 'all';
      const currentWorkingCompany = profile?.workingCompany || localStorage.getItem('workingCompany') || 'all';

      if (currentClientName !== 'all' && currentClientName.toLowerCase() !== 'infinity') {
        const hasRelevantMember = conv.members?.some(m =>
          (!m.clientName || m.clientName === currentClientName) &&
          (currentWorkingCompany === 'all' || !m.workingCompany || m.workingCompany === currentWorkingCompany)
        );
        if (conv.members && conv.members.length > 0 && !hasRelevantMember) {
          return false;
        }
      }
    }

    const alias = chatPrefs.getAlias(conv.id);
    const nameToSearch = [alias, conv.name, conv.arabicName, conv.fullName, conv.employeeName].filter(Boolean).join(" ").toLowerCase();
    const q = searchQuery.toLowerCase();
    return nameToSearch.includes(q) || (conv.lastMessage || '').toLowerCase().includes(q);
  });

  // Pinned chats float to the top (Array.sort is stable, so recency order is
  // preserved inside each bucket).
  const displayConversations = [...filteredConversations].sort((a, b) => {
    const pa = chatPrefs.isPinned(a.id) ? 1 : 0;
    const pb = chatPrefs.isPinned(b.id) ? 1 : 0;
    return pb - pa;
  });

  const groupsCount = conversations.filter((conv) => isGroupConv(conv) && !conv.isArchived).length;

  const conversationIds = new Set(conversations.map((item) => item.id));

  const deferredSearchQuery = useDeferredValue(searchQuery);
  const filteredEmployees = useMemo(() => initState.allEmployees.filter((emp) => {
    if (activeTab === 'groups') return false; // Don't show employees in Groups tab
    if (!deferredSearchQuery) return true; // Show all employees when no search query
    if (isSameUser(emp.id, currentUser?.infinityCode)) return false;
    if (conversationIds.has(emp.id)) return false; // Don't show in all employees if they already have a conversation

    // Search by English name
    const nameToSearch = (emp.englishName || emp.name || '').toLowerCase();
    const q = deferredSearchQuery.toLowerCase();

    return nameToSearch.includes(q);
  }).slice(0, MAX_RENDERED_RESULTS), [initState.allEmployees, activeTab, deferredSearchQuery, currentUser?.infinityCode, conversationIds]);

  // Direct-message and group-member pickers filter the full employee
  // directory (1000s of rows) on every keystroke. Deferring the search
  // value (see MAX_RENDERED_RESULTS/useDeferredValue above) keeps the input
  // itself responsive; capping the rendered rows keeps the eventual list
  // render itself fast instead of mounting a thousand+ DOM nodes at once.
  const directChatCandidates = useMemo(() => {
    const q = deferredNewChatSearch.toLowerCase();
    const matches = initState.allEmployees.filter((emp) =>
      !isSameUser(emp.id, currentUser?.infinityCode) && (emp.englishName || emp.name || '').toLowerCase().includes(q)
    );
    return { items: matches.slice(0, MAX_RENDERED_RESULTS), total: matches.length };
  }, [initState.allEmployees, deferredNewChatSearch, currentUser?.infinityCode]);

  const groupMemberCandidates = useMemo(() => {
    const q = deferredNewGroupSearch.toLowerCase();
    const matches = initState.allEmployees.filter((emp) =>
      !isSameUser(emp.id, currentUser?.infinityCode) && (emp.englishName || emp.name || '').toLowerCase().includes(q)
    );
    return { items: matches.slice(0, MAX_RENDERED_RESULTS), total: matches.length };
  }, [initState.allEmployees, deferredNewGroupSearch, currentUser?.infinityCode]);

  const toggleGroupMember = (employee) => {
    setGroupMembers((prev) => {
      const exists = prev.some((member) => isSameUser(member.id, employee.id));
      return exists
        ? prev.filter((member) => !isSameUser(member.id, employee.id))
        : [...prev, employee];
    });
  };

  // Messenger Phase 1: load the department list (id + name) for the "sync to
  // department" picker, once per modal session — only when Department Sync
  // is selected and the user is permitted to create org-synced groups.
  useEffect(() => {
    if (!showNewChatModal || newChatModalTab !== 'group') return;
    if (groupMembershipMode !== 'DEPARTMENT_SYNC') return;
    if (syncDepartments.length > 0 || syncDepartmentsLoading) return;

    const clientName = profile?.clientName || localStorage.getItem('clientName') || '';
    const workingCompany = profile?.workingCompany || localStorage.getItem('workingCompany') || '';
    if (!clientName) return;

    setSyncDepartmentsLoading(true);
    const params = new URLSearchParams({ clientName, ...(workingCompany && { workingCompany }) });
    apiFetch(`${API_BASE_URL}/api/departments?${params}`)
      .then((res) => res.json())
      .then((body) => setSyncDepartments(Array.isArray(body?.data) ? body.data : []))
      .catch((err) => console.error('Failed to load departments for sync group:', err))
      .finally(() => setSyncDepartmentsLoading(false));
  }, [showNewChatModal, newChatModalTab, groupMembershipMode, syncDepartments.length, syncDepartmentsLoading, profile]);

  // Same pattern as the department picker, for "sync to company". Companies
  // aren't {id,name} rows — detect-companies returns {companyName, ...}
  // objects scoped to the current user's own clientName.
  useEffect(() => {
    if (!showNewChatModal || newChatModalTab !== 'group') return;
    if (groupMembershipMode !== 'COMPANY_SYNC') return;
    if (syncCompanies.length > 0 || syncCompaniesLoading) return;

    const clientName = profile?.clientName || localStorage.getItem('clientName') || '';
    if (!clientName) return;

    setSyncCompaniesLoading(true);
    apiFetch(`${API_BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/detect-companies`)
      .then((res) => res.json())
      .then((body) => setSyncCompanies(Array.isArray(body?.data?.companies) ? body.data.companies : []))
      .catch((err) => console.error('Failed to load companies for sync group:', err))
      .finally(() => setSyncCompaniesLoading(false));
  }, [showNewChatModal, newChatModalTab, groupMembershipMode, syncCompanies.length, syncCompaniesLoading, profile]);

  const resetGroupCreationState = () => {
    setGroupName('');
    setGroupMembers([]);
    setGroupMembershipMode('MANUAL');
    setGroupPostingMode('EVERYONE');
    setGroupSyncDepartmentId(null);
    setGroupSyncWorkingCompany(null);
    setGroupAutoAddNewMembers(true);
    setSyncDepartments([]);
    setSyncCompanies([]);
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setAlertState({ open: true, title: t.error, message: t.groupName, variant: 'error' });
      return;
    }
    // A Department-Sync group's roster is populated by the sync itself
    // (plan §7.4) — manual member picks are optional there, only required
    // for a plain Manual group.
    if (groupMembershipMode === 'MANUAL' && groupMembers.length < 2) {
      setAlertState({ open: true, title: t.error, message: t.selectGroupMembers, variant: 'error' });
      return;
    }
    if (groupMembershipMode === 'DEPARTMENT_SYNC' && !groupSyncDepartmentId) {
      setAlertState({ open: true, title: t.error, message: t.selectDepartmentToSync, variant: 'error' });
      return;
    }
    if (groupMembershipMode === 'COMPANY_SYNC' && !groupSyncWorkingCompany) {
      setAlertState({ open: true, title: t.error, message: t.selectCompanyToSync, variant: 'error' });
      return;
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/chat-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: groupName.trim(),
          members: groupMembers.map((member) => member.id),
          membershipMode: groupMembershipMode,
          postingMode: groupPostingMode,
          ...(groupMembershipMode === 'DEPARTMENT_SYNC' && { syncDepartmentId: groupSyncDepartmentId }),
          ...(groupMembershipMode === 'COMPANY_SYNC' && { syncWorkingCompany: groupSyncWorkingCompany }),
          ...((groupMembershipMode === 'DEPARTMENT_SYNC' || groupMembershipMode === 'COMPANY_SYNC') && { autoAddNewMembers: groupAutoAddNewMembers }),
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to create group');
      }

      const createdGroup = await response.json();
      const groupId = `group-${createdGroup.id}`;

      if (createdGroup.skippedMembers?.length > 0) {
        setAlertState({
          open: true,
          title: t.error,
          message: isRTL
            ? `تم إنشاء الجروب، لكن ${createdGroup.skippedMembers.length} من الأعضاء المختارين لم يعد لهم وجود وتم تجاهلهم.`
            : `Group created, but ${createdGroup.skippedMembers.length} selected member(s) no longer exist and were skipped.`,
          variant: 'error',
        });
      }

      const group = {
        id: groupId,
        name: createdGroup.name,
        avatar: createdGroup.name
          .trim()
          .split(' ')
          .map((word) => word[0])
          .join('')
          .slice(0, 2)
          .toUpperCase(),
        online: false,
        company: '',
        department: '',
        position: '',
        email: '',
        arabicName: '',
        isGroup: true,
        createdBy: currentUser?.infinityCode,
        membershipMode: createdGroup.membershipMode || 'MANUAL',
        postingMode: createdGroup.postingMode || 'EVERYONE',
        syncDepartmentId: createdGroup.syncDepartmentId ?? null,
        syncWorkingCompany: createdGroup.syncWorkingCompany ?? null,
        autoAddNewMembers: createdGroup.autoAddNewMembers ?? true,
        members: createdGroup.members?.map((m) => ({
          id: m.infinityCode,
          name: m.employee?.sr || m.employee?.name || `User ${m.infinityCode}`,
          role: m.role
        })) || [],
        lastMessage: '',
        time: t.justNow,
        timestamp: Date.now(),
        unread: 0,
      };

      setConversations((prev) => {
        // Prevent duplicate: check if this group was already added by the socket handler
        if (prev.some(c => c.id === groupId)) return prev;
        return [group, ...prev];
      });
      setMessages((prev) => ({ ...prev, [groupId]: [] }));
      initState.setSelectedUser(group);
      setShowNewChatModal(false);
      resetGroupCreationState();
      setShowRightPanel(false);
    } catch (err) {
      console.error('Error creating group:', err);
      setAlertState({ open: true, title: t.error, message: err.message || 'Failed to create group. Please try again.', variant: 'error' });
    }
  };

  // Messenger Phase 1: fetch { lastSyncAt, lastSyncStatus, lastSyncError,
  // memberCount } for the sync-status badge (GET /:groupId/sync-status).
  const fetchGroupSyncStatus = async (group) => {
    const actualGroupId = String(group.id).replace('group-', '');
    setGroupSyncStatusLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/sync-status`);
      if (res.ok) {
        const body = await res.json();
        setGroupSyncStatus(body.data || null);
      } else {
        setGroupSyncStatus(null);
      }
    } catch (err) {
      console.error('Failed to fetch group sync status:', err);
      setGroupSyncStatus(null);
    } finally {
      setGroupSyncStatusLoading(false);
    }
  };

  const openGroupSettingsModal = (group) => {
    setGroupSettingsTarget(group);
    setSettingsPostingMode(group.postingMode || 'EVERYONE');
    setSettingsMembershipMode(group.membershipMode || 'MANUAL');
    setSettingsSyncDepartmentId(group.syncDepartmentId ?? null);
    setSettingsSyncWorkingCompany(group.syncWorkingCompany ?? null);
    setSettingsAutoAddNewMembers(group.autoAddNewMembers ?? true);
    setSettingsSyncDepartments([]);
    setSettingsSyncCompanies([]);
    setGroupSyncStatus(null);
    setShowGroupSettingsModal(true);

    if (group.membershipMode === 'DEPARTMENT_SYNC' || group.membershipMode === 'COMPANY_SYNC') {
      fetchGroupSyncStatus(group);
    }
  };

  // Loads the department picker (only reachable for canCreateOrgGroup users,
  // since only they can switch membershipMode into DEPARTMENT_SYNC) — same
  // endpoint/shape as the group-creation picker.
  useEffect(() => {
    if (!showGroupSettingsModal || settingsMembershipMode !== 'DEPARTMENT_SYNC') return;
    if (settingsSyncDepartments.length > 0 || settingsSyncDepartmentsLoading) return;

    const clientName = profile?.clientName || localStorage.getItem('clientName') || '';
    const workingCompany = profile?.workingCompany || localStorage.getItem('workingCompany') || '';
    if (!clientName) return;

    setSettingsSyncDepartmentsLoading(true);
    const params = new URLSearchParams({ clientName, ...(workingCompany && { workingCompany }) });
    apiFetch(`${API_BASE_URL}/api/departments?${params}`)
      .then((res) => res.json())
      .then((body) => setSettingsSyncDepartments(Array.isArray(body?.data) ? body.data : []))
      .catch((err) => console.error('Failed to load departments for group settings:', err))
      .finally(() => setSettingsSyncDepartmentsLoading(false));
  }, [showGroupSettingsModal, settingsMembershipMode, settingsSyncDepartments.length, settingsSyncDepartmentsLoading, profile]);

  // Same pattern, for the company picker in Group Settings.
  useEffect(() => {
    if (!showGroupSettingsModal || settingsMembershipMode !== 'COMPANY_SYNC') return;
    if (settingsSyncCompanies.length > 0 || settingsSyncCompaniesLoading) return;

    const clientName = profile?.clientName || localStorage.getItem('clientName') || '';
    if (!clientName) return;

    setSettingsSyncCompaniesLoading(true);
    apiFetch(`${API_BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/detect-companies`)
      .then((res) => res.json())
      .then((body) => setSettingsSyncCompanies(Array.isArray(body?.data?.companies) ? body.data.companies : []))
      .catch((err) => console.error('Failed to load companies for group settings:', err))
      .finally(() => setSettingsSyncCompaniesLoading(false));
  }, [showGroupSettingsModal, settingsMembershipMode, settingsSyncCompanies.length, settingsSyncCompaniesLoading, profile]);

  const closeGroupSettingsModal = () => {
    setShowGroupSettingsModal(false);
    setGroupSettingsTarget(null);
    setGroupSyncStatus(null);
  };

  const handleSaveGroupSettings = async () => {
    if (!groupSettingsTarget) return;
    const actualGroupId = String(groupSettingsTarget.id).replace('group-', '');

    const body = { postingMode: settingsPostingMode };
    // Only send membershipMode/syncDepartmentId/syncWorkingCompany/
    // autoAddNewMembers if this user is allowed to change them — the fields
    // are hidden from non-createOrgGroup users, but guard here too so a
    // stale draft never gets submitted by accident.
    if (canCreateOrgGroup) {
      body.membershipMode = settingsMembershipMode;
      if (settingsMembershipMode === 'DEPARTMENT_SYNC') {
        body.syncDepartmentId = settingsSyncDepartmentId;
      }
      if (settingsMembershipMode === 'COMPANY_SYNC') {
        body.syncWorkingCompany = settingsSyncWorkingCompany;
      }
      if (settingsMembershipMode === 'DEPARTMENT_SYNC' || settingsMembershipMode === 'COMPANY_SYNC') {
        body.autoAddNewMembers = settingsAutoAddNewMembers;
      }
    }

    setSavingGroupSettings(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || 'Failed to update group settings');
      }
      const result = await res.json();
      const updatedGroup = result.data?.group || {};
      const patch = {
        postingMode: updatedGroup.postingMode ?? settingsPostingMode,
        membershipMode: updatedGroup.membershipMode ?? settingsMembershipMode,
        syncDepartmentId: updatedGroup.syncDepartmentId ?? settingsSyncDepartmentId,
        syncWorkingCompany: updatedGroup.syncWorkingCompany ?? settingsSyncWorkingCompany,
        autoAddNewMembers: updatedGroup.autoAddNewMembers ?? settingsAutoAddNewMembers,
      };

      const fullGroupId = groupSettingsTarget.id;
      setConversations((prev) => prev.map((conv) => (conv.id === fullGroupId ? { ...conv, ...patch } : conv)));
      if (initState.selectedUser?.id === fullGroupId) {
        initState.setSelectedUser((prev) => ({ ...prev, ...patch }));
      }

      setAlertState({ open: true, title: t.success, message: t.groupSettingsSaved, variant: 'success' });
      closeGroupSettingsModal();
    } catch (err) {
      console.error('Error saving group settings:', err);
      setAlertState({ open: true, title: t.error, message: err.message || 'Failed to save group settings', variant: 'error' });
    } finally {
      setSavingGroupSettings(false);
    }
  };

  const handleRemoveMember = async (memberCode) => {
    try {
      const actualGroupId = selectedUser.id.replace('group-', '');
      const response = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/members/${memberCode}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to remove member');
      }

      setConversations((prev) =>
        prev.map((c) => {
          if (c.id === selectedUser.id) {
            return {
              ...c,
              members: (c.members || []).filter((m) => !isSameUser(m.id, memberCode)),
            };
          }
          return c;
        })
      );

      initState.setSelectedUser((prev) => ({
        ...prev,
        members: (prev.members || []).filter((m) => !isSameUser(m.id, memberCode)),
      }));

    } catch (err) {
      console.error('Error removing member:', err);
      setAlertState({
        open: true,
        title: t.error,
        message: isRTL ? 'فشل في حذف العضو' : 'Failed to remove member. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleAddMember = async (memberCode) => {
    try {
      const actualGroupId = selectedUser.id.replace('group-', '');
      const response = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ members: [memberCode] }),
      });

      if (!response.ok) {
        throw new Error('Failed to add member');
      }

      const emp = initState.allEmployees.find((e) => isSameUser(e.id, memberCode));
      const newMember = {
        id: memberCode,
        name: emp?.name || `User ${memberCode}`,
        role: 'member',
      };

      setConversations((prev) =>
        prev.map((c) => {
          if (c.id === selectedUser.id) {
            const currentMembers = c.members || [];
            if (currentMembers.some((m) => isSameUser(m.id, memberCode))) return c;
            return {
              ...c,
              members: [...currentMembers, newMember],
            };
          }
          return c;
        })
      );

      initState.setSelectedUser((prev) => {
        const currentMembers = prev.members || [];
        if (currentMembers.some((m) => isSameUser(m.id, memberCode))) return prev;
        return {
          ...prev,
          members: [...currentMembers, newMember],
        };
      });

    } catch (err) {
      console.error('Error adding member:', err);
      setAlertState({
        open: true,
        title: t.error,
        message: isRTL ? 'فشل في إضافة العضو' : 'Failed to add member. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleSetAdminRole = async (memberId, isAdmin) => {
    try {
      const actualGroupId = selectedUser.id.replace('group-', '');
      const response = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/members/${memberId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isAdmin }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update role');
      }

      // Update local state
      const updateMemberRole = (members) =>
        members.map((m) => (m.id === memberId ? { ...m, role: isAdmin ? 'admin' : 'member' } : m));

      initState.setSelectedUser((prev) => ({
        ...prev,
        members: updateMemberRole(prev.members || []),
      }));

      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedUser.id ? { ...c, members: updateMemberRole(c.members || []) } : c
        )
      );
    } catch (err) {
      setAlertState({
        open: true,
        title: t.error || 'Error',
        message: err.message,
        variant: 'error',
      });
    }
  };

  const handleSelectUser = async (user) => {
    if (isGroupConv(user)) {
      initState.setSelectedUser(user);
      setShowRightPanel(false);
      // Fetch group messages if not already loaded
      if (!messages[user.id] || messages[user.id].length === 0) {
        await messagesState.fetchMessagesForUser(user.id);
      }
      // Clear unread badge
      setConversations((prev) => prev.map((c) =>
        isSameUser(c.id, user.id) ? { ...c, unread: 0 } : c
      ));

      // The read receipt itself is sent by useReadReceipts once the thread
      // is open (and the tab visible).

      return;
    }
    await initState.handleUserSelect(user);
    setShowRightPanel(false);
  };

  // Notification deep-link (?chat=<infinityCode>): open that conversation
  // directly — falls back to starting a fresh one when no thread exists yet.
  const chatDeepLinkConsumedRef = useRef(false);
  useEffect(() => {
    const chatWith = new URLSearchParams(window.location.search).get('chat');
    if (!chatWith || chatDeepLinkConsumedRef.current) return;
    if (!currentUser?.infinityCode) return;
    const conversation = conversations.find((conv) => isSameUser(conv.id, chatWith));
    if (conversation) {
      chatDeepLinkConsumedRef.current = true;
      handleSelectUser(conversation);
      return;
    }
    const employee = (initState.allEmployees || []).find((emp) => isSameUser(emp.id, chatWith));
    if (employee) {
      chatDeepLinkConsumedRef.current = true;
      initState.startNewConversation(employee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, initState.allEmployees, currentUser]);

  useEffect(() => {
    if (initState.selectedUser) {
      setShowRightPanel(false);
    }
  }, [initState.selectedUser]);

  // Reset per-chat UI state (reply draft, in-chat search, lightbox, alias
  // editor) whenever the open thread changes.
  useEffect(() => {
    setReplyingTo(null);
    setChatSearchOpen(false);
    setChatSearchQuery('');
    setChatSearchIndex(0);
    setViewerState(null);
    setAliasEditing(false);
    setListInputOpen(false);
  }, [initState.selectedUser?.id]);

  // Close the sidebar 3-dots menu on outside click.
  useEffect(() => {
    if (!showSidebarMenu) return;
    const handler = (e) => {
      if (sidebarMenuRef.current && !sidebarMenuRef.current.contains(e.target)) {
        setShowSidebarMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSidebarMenu]);

  // Auto-select first conversation on desktop if none selected
  // Removed per user request so it doesn't open the first chat automatically
  /*
  useEffect(() => {
    if (!isMobile && !initState.selectedUser && conversations.length > 0 && !initState.loading) {
      handleSelectUser(conversations[0]);
    }
  }, [isMobile, initState.selectedUser, conversations, initState.loading]);
  */

  const isUserOnline = (userId) => {
    if (!userId || !onlineUsers) return false;
    if (onlineUsers instanceof Set) {
      return Array.from(onlineUsers).some((id) => isSameUser(id, userId));
    }
    if (Array.isArray(onlineUsers)) {
      return onlineUsers.some((id) => isSameUser(id, userId));
    }
    return false;
  };

  const sidebarVisible = !(initState.selectedUser && isMobile);

  // ── Drag-to-resize sidebar ────────────────────────────────────────────
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleResizeMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      const delta = isRTL
        ? startXRef.current - moveEvent.clientX
        : moveEvent.clientX - startXRef.current;
      const newWidth = Math.min(600, Math.max(280, startWidthRef.current + delta));
      setSidebarWidth(newWidth);
    };

    const handleResizeEnd = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setSidebarWidth((w) => {
          localStorage.setItem('messengerSidebarWidth', String(w));
          return w;
        });
      }
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [sidebarWidth, isRTL]);

  const styles = getResponsiveStyles({ theme, c, isRTL, isMobile, initState, showRightPanel, sidebarCollapsed: isCollapsed, sidebarVisible, sidebarWidthPx: sidebarWidth });

  const selectedUser = initState.selectedUser;
  const selectedUserOnline = selectedUser ? isUserOnline(selectedUser.id) : false;

  const handleDeleteWithConfirm = (message) => {
    setAlertState({
      open: true,
      title: isRTL ? 'حذف الرسالة' : 'Delete Message',
      message: isRTL ? 'هل أنت متأكد من حذف هذه الرسالة؟' : 'Are you sure you want to delete this message?',
      variant: 'error',
      showCancel: true,
      onConfirm: () => {
        setAlertState((prev) => ({ ...prev, open: false }));
        messagesState.handleDeleteMessage(message);
      },
    });
  };

  // ── WhatsApp-style feature helpers ─────────────────────────────────────

  // Resolve an infinityCode to a display name (reactions tooltips, reply
  // quotes). Checks the open chat's members, then conversations, then self.
  const resolveReactorName = useCallback((code) => {
    if (isSameUser(code, currentUser?.infinityCode)) return t.you;
    const su = initState.selectedUser;
    if (su?.members) {
      const member = su.members.find((m) => isSameUser(m.id, code));
      if (member) return member.name;
    }
    const conv = conversations.find((cv) => isSameUser(cv.id, code));
    if (conv) return chatPrefs.getAlias(conv.id) || conv.name;
    const emp = initState.allEmployees.find((e) => isSameUser(e.id, code));
    return emp?.name || `User ${code}`;
  }, [conversations, currentUser, initState.selectedUser, initState.allEmployees, chatPrefs, t.you]);

  // Snapshot stored in localStorage for the Starred Messages page.
  const buildStarSnapshot = (message) => ({
    id: message.id,
    chatId: initState.selectedUser?.id,
    chatName: chatPrefs.getAlias(initState.selectedUser?.id) || initState.selectedUser?.name,
    text: message.text || null,
    type: message.type || 'text',
    mediaName: message.mediaName || null,
    senderName: message.sent ? null : (message.sender?.name || initState.selectedUser?.name),
    sent: !!message.sent,
    time: message.time,
  });

  const handleToggleStar = (message) => chatPrefs.toggleStarMessage(buildStarSnapshot(message));

  const handleOpenStarredChat = (snapshot) => {
    const conv = conversations.find((cv) => isSameUser(cv.id, snapshot.chatId) || String(cv.id) === String(snapshot.chatId));
    setShowStarredModal(false);
    if (conv) {
      handleSelectUser(conv);
      // Best-effort: the message may not be in the loaded window.
      setTimeout(() => setJumpToMessageId(snapshot.id), 600);
    }
  };

  const handleForwardConfirm = async (targets) => {
    if (!forwardMsg) return;
    try {
      await messagesState.forwardMessage(forwardMsg, targets);
      setForwardMsg(null);
    } catch (err) {
      console.error('Forward failed:', err);
      setAlertState({ open: true, title: t.error || t.errorTitle, message: t.sendFailed, variant: 'error' });
    }
  };

  // Unified lightbox: every chat image is navigable inside the same viewer.
  const openMediaViewer = (message) => setViewerState({ message });

  const currentChatMessages = initState.selectedUser
    ? messages[String(initState.selectedUser.id)] || messages[initState.selectedUser.id] || []
    : [];
  const chatImages = currentChatMessages.filter((m) => m.type === 'image' && m.mediaUrl);
  const viewerImageIndex = viewerState?.message?.type === 'image'
    ? chatImages.findIndex((m) => String(m.id) === String(viewerState.message.id))
    : -1;

  // In-chat search: ordered ids of messages whose text matches.
  const chatSearchMatches = chatSearchQuery
    ? currentChatMessages
      .filter((m) => (m.text || '').toLowerCase().includes(chatSearchQuery.toLowerCase()))
      .map((m) => m.id)
    : [];

  const navigateChatSearch = (direction) => {
    if (!chatSearchMatches.length) return;
    // Matches are chronological; "next" walks backwards in time like WhatsApp.
    const next = (chatSearchIndex + (direction === 1 ? 1 : -1) + chatSearchMatches.length) % chatSearchMatches.length;
    setChatSearchIndex(next);
    setJumpToMessageId(chatSearchMatches[chatSearchMatches.length - 1 - next]);
  };

  // Groups shared between me and the open contact.
  const commonGroups = selectedUser && !isGroupConv(selectedUser)
    ? conversations.filter((cv) =>
      isGroupConv(cv) && !cv.isArchived &&
      cv.members?.some((m) => isSameUser(m.id, selectedUser.id)) &&
      cv.members?.some((m) => isSameUser(m.id, currentUser?.infinityCode)))
    : [];

  const selectedIsBlocked = selectedUser ? chatPrefs.isBlocked(selectedUser.id) : false;
  const selectedDisplayName = selectedUser
    ? (chatPrefs.getAlias(selectedUser.id) || selectedUser.englishName || selectedUser.name)
    : '';

  // Messenger Phase 1: in an ADMINS_ONLY group, only admins may post — the
  // composer itself is hidden (not just disabled) for everyone else, with an
  // inline note explaining why instead of the composer just vanishing.
  const postingModeBlocksMe = isGroupConv(selectedUser) && selectedUser?.postingMode === 'ADMINS_ONLY' && !isCurrentUserAdminOfSelectedGroup;

  let activeMembersCount = 0;
  if (selectedUser && isGroupConv(selectedUser) && selectedUser.members) {
    // Also consider if current user is active? We probably just count anyone in the members list that is online.
    activeMembersCount = selectedUser.members.filter(m => isUserOnline(m.id)).length;
  }
  const selectedMessages = selectedUser
    ? messages[String(selectedUser.id)] || messages[selectedUser.id] || []
    : [];
  const imagesCount = selectedMessages.filter((msg) => msg.type === 'image').length;
  const docsCount = selectedMessages.filter((msg) =>
    ['pdf', 'excel', 'word', 'powerpoint'].includes(msg.type)
  ).length;
  const voiceCount = selectedMessages.filter((msg) => msg.type === 'voice').length;
  const sharedMedia = selectedMessages.filter((msg) => msg.type === 'image').slice(-6).reverse();

  // ── Feature "view" (registry: messages.view) — admin-view users without it
  // get an empty state instead of the inbox. Page-level routing guards handle
  // the module itself; this covers the section-level toggle.
  if (!canViewMessages) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorContainer}>
          <AlertCircle size={48} style={{ marginBottom: '16px', color: '#ef4444' }} />
          <div style={styles.errorTitle}>
            {isRTL ? 'لا توجد صلاحيات' : 'Access Denied'}
          </div>
          <div style={styles.errorMessage}>
            {isRTL
              ? 'ليس لديك صلاحية لعرض الرسائل'
              : "You don't have permission to view messages"}
          </div>
        </div>
      </div>
    );
  }

  // Wait until conversations are loaded
  const isReadyToRender = !initState.loading;

  if (!isReadyToRender && !initState.error) {
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <LoadingPill
              text={isRTL ? "جارٍ تحميل المحادثات..." : "Loading conversations..."}
              theme={theme}
              isRTL={isRTL}
            />
            <div style={styles.searchContainer}>
              <SkeletonBox width="100%" height="42px" style={{ borderRadius: '12px' }} />
            </div>
          </div>
          <div style={styles.usersList}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0' }}>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' }}>
                  <SkeletonBox width="52px" height="52px" style={{ borderRadius: '50%', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <SkeletonBox width="70%" height="16px" style={{ marginBottom: '8px' }} />
                    <SkeletonBox width="45%" height="13px" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ ...styles.chatArea, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SkeletonBox width="64px" height="64px" style={{ borderRadius: '50%' }} />
        </div>
      </div>
    );
  }

  if (initState.error && conversations.length === 0 && initState.allEmployees.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorContainer}>
          <AlertCircle size={48} style={{ marginBottom: '16px', color: '#ef4444' }} />
          <div style={styles.errorTitle}>{t.errorTitle}</div>
          <div style={styles.errorMessage}>{initState.error}</div>
          <button
            onClick={() =>
              initState.initializeMessaging((authToken, user) =>
                socketState.initializeSocket(authToken, user, {
                  handleIncomingMessage: messagesState.handleIncomingMessage,
                  setTypingUsers,
                  markMessagesAsRead: messagesState.markMessagesAsRead,
                  setOnlineUsers,
                  setIsConnected,
                  onError: initState.setError,
                })
              )
            }
            style={styles.retryButton}
          >
            {t.retry}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.container}>
        <div style={styles.sidebar}>
          {(
            <div style={styles.sidebarHeader}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h1 style={styles.sidebarTitle}>{t.messages}</h1>
                  {!(socketState.isConnected || contextIsConnected) && (
                    <span
                      style={{
                        fontSize: '10px',
                        color: '#ef4444',
                        fontWeight: '500',
                        padding: '4px 10px',
                        background: '#fee2e2',
                        borderRadius: '12px',
                      }}
                    >
                      Offline
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {canSendMessages && (
                  <button
                    type="button"
                    className="btn-gradient"
                    onClick={() => setShowNewChatModal(true)}
                    title={t.newChat}
                    aria-label={t.newChat}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <MessageSquare size={18} />
                  </button>
                  )}
                  <div ref={sidebarMenuRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setShowSidebarMenu((p) => !p)}
                      title={t.moreOptions}
                      aria-label={t.moreOptions}
                      aria-expanded={showSidebarMenu}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '12px',
                        border: `1px solid ${showSidebarMenu ? '#2e7cba' : c.border}`,
                        background: c.inputBg,
                        color: showSidebarMenu ? '#2e7cba' : c.textSecondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <MoreVertical size={17} />
                    </button>
                    {showSidebarMenu && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          insetInlineEnd: 0,
                          zIndex: 300,
                          minWidth: '210px',
                          background: c.popoverBg || c.sidebarBg,
                          border: `1px solid ${c.popoverBorder || c.border}`,
                          borderRadius: '16px',
                          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.22)',
                          padding: '8px',
                          animation: 'popoverSlideUp 0.15s ease',
                        }}
                      >
                        {canStarMessages && (
                        <button
                          type="button"
                          onClick={() => { setShowSidebarMenu(false); setShowStarredModal(true); }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            border: 'none',
                            background: 'transparent',
                            color: c.text,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '13.5px',
                            fontWeight: '600',
                            textAlign: 'start',
                          }}
                        >
                          <Star size={16} color="#f59e0b" />
                          {t.starredMessages}
                        </button>
                        )}
                        {conversations.some((conv) => conv.isArchived) && (
                          <button
                            type="button"
                            onClick={() => { setShowSidebarMenu(false); setActiveTab('archived'); }}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 12px',
                              border: 'none',
                              background: activeTab === 'archived' ? (theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9') : 'transparent',
                              color: c.text,
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontSize: '13.5px',
                              fontWeight: '600',
                              textAlign: 'start',
                            }}
                          >
                            <Archive size={16} color={c.textSecondary} />
                            {t.tabArchived}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              </div>

              <div style={styles.searchContainer}>
                <Search style={styles.searchIcon} />
                <input
                  type="text"
                  placeholder={isRTL ? 'بحث عن شخص أو جروب...' : 'Search people or groups...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
              {/* Filter Tabs — Chats (People) / Groups (count) / Unread / Favorites (+Archived) */}
              <div style={{ padding: '0 20px 16px', borderBottom: `1px solid ${c.border}`, marginTop: "12px" }}>
                <div style={{ display: 'flex', background: theme === 'dark' ? c.inputBg : '#e2e8f0', borderRadius: '14px', padding: '4px' }}>
                  {[
                    { id: 'all', label: t.tabAll },
                    { id: 'messages', label: t.tabChats },
                    ...(conversations.some(conv => isGroupConv(conv))
                      ? [{ id: 'groups', label: groupsCount > 0 ? `${t.tabGroups} (${groupsCount})` : t.tabGroups }]
                      : []),
                    { id: 'unread', label: t.tabUnread },
                    { id: 'favorites', label: t.tabFavorites },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        flex: 1, padding: '8px 4px', borderRadius: '10px', border: 'none',
                        background: activeTab === tab.id ? (theme === 'dark' ? c.sidebarBg : '#ffffff') : 'transparent',
                        color: activeTab === tab.id ? c.text : c.textSecondary,
                        fontWeight: activeTab === tab.id ? '600' : '500', cursor: 'pointer', transition: 'all 0.2s', fontSize: '12.5px',
                        boxShadow: activeTab === tab.id ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div style={styles.usersList}>
            <ConversationList
              conversations={displayConversations}
              selectedUser={initState.selectedUser}
              employeeProfilePics={employeeProfilePics}
              onSelect={handleSelectUser}
              isRTL={isRTL}
              theme={theme}
              c={c}
              styles={styles}
              getAvatarColor={getAvatarColor}
              isSameUser={isSameUser}
              t={t}
              // Pass the live Set so the row dot reacts to user:online /
              // user:offline events without waiting for setConversations to
              // re-snapshot the `online` field on the next message.
              onlineUsers={onlineUsers}
              getAlias={chatPrefs.getAlias}
              isPinned={chatPrefs.isPinned}
              isMuted={chatPrefs.isMuted}
            />

            {/* <EmployeeList
              employees={filteredEmployees}
              employeeProfilePics={employeeProfilePics}
              onSelect={initState.startNewConversation}
              c={c}
              styles={styles}
              getAvatarColor={getAvatarColor}
              getTrilateralName={getTrilateralName}
              t={t}
              hasConversations={filteredConversations.length > 0}
            /> */}

            {filteredConversations.length === 0 && filteredEmployees.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: c.textSecondary }}>
                <Users size={56} style={{ marginBottom: '16px', opacity: 0.4 }} />
                <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: c.text }}>
                  {searchQuery ? t.noResults : t.noEmployees}
                </p>
                {searchQuery && <p style={{ fontSize: '14px', color: c.textSecondary }}>{t.tryDifferent}</p>}
              </div>
            )}
          </div>


        </div>

        <div style={styles.chatArea}>
          {showEditGroupModal && selectedUser && (
            <div
              onClick={() => setShowEditGroupModal(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 1000, background: theme === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', backdropFilter: 'blur(4px)'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', maxWidth: 520, background: theme === 'dark' ? '#1e293b' : '#ffffff', borderRadius: '24px',
                  boxShadow: c.shadow, padding: '24px', overflowY: 'hidden', height: '80vh',
                  border: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: c.text }}>
                    {isRTL ? `تعديل الجروب: ${selectedUser.name}` : `Edit Group: ${selectedUser.name}`}
                  </h3>
                  <button type="button" onClick={() => setShowEditGroupModal(false)}
                    style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', fontSize: '20px' }}>
                    ×
                  </button>
                </div>

                <div style={{ marginBottom: '16px', position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: isRTL ? 'auto' : '14px', right: isRTL ? '14px' : 'auto', top: '50%', transform: 'translateY(-50%)', color: c.textSecondary }} />
                  <input
                    type="text"
                    value={editGroupSearch}
                    onChange={(e) => setEditGroupSearch(e.target.value)}
                    placeholder={isRTL ? 'بحث عن موظفين لإضافتهم...' : 'Search employees to add...'}
                    style={{
                      width: '100%',
                      padding: `12px ${isRTL ? '40px' : '14px'} 12px ${isRTL ? '14px' : '40px'}`,
                      borderRadius: '14px',
                      border: `1px solid ${c.border}`,
                      background: c.inputBg,
                      color: c.text,
                      outline: 'none'
                    }}
                  />
                </div>

                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px', paddingRight: '4px' }}>

                  <div>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: c.text }}>
                      {isRTL ? 'الأعضاء الحاليين' : 'Current Members'} ({selectedUser.members?.length || 0})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(() => {
                        const sortedCurrent = [...(selectedUser.members || [])].sort((a, b) => {
                          if (a.role === 'admin' && b.role !== 'admin') return -1;
                          if (b.role === 'admin' && a.role !== 'admin') return 1;
                          return 0;
                        });
                        return sortedCurrent.map(member => {
                          const isAdmin = member.role === 'admin';
                          const isMe = isSameUser(member.id, currentUser?.infinityCode);
                          const initials = member.name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                          const avatarSrc = employeeProfilePics[member.id];
                          return (
                            <div
                              key={member.id}
                              className="employee-item-card"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 12px',
                                borderRadius: '14px'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                  width: '32px', height: '32px', borderRadius: '50%',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: avatarSrc ? 'transparent' : getAvatarColor(member.name),
                                  color: '#fff', fontSize: '12px', fontWeight: 'bold',
                                  border: isAdmin ? '2px solid #2e7cba' : 'none',
                                  boxSizing: 'border-box'
                                }}>
                                  {avatarSrc ? (
                                    <img src={avatarSrc} alt={member.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                  ) : <User size={16} />}
                                </div>
                                <span style={{ fontWeight: '600', fontSize: '13px', color: c.text }}>
                                  {member.name} {isMe && (isRTL ? '(أنت)' : '(You)')}
                                </span>
                              </div>
                              {isAdmin ? (
                                <span style={{ fontSize: '11px', color: '#2e7cba', fontWeight: '700', padding: '4px 8px', background: theme === 'dark' ? 'rgba(46, 124, 186, 0.15)' : '#e0f0ff', borderRadius: '6px' }}>
                                  {isRTL ? 'أدمن' : 'Admin'}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAlertState({
                                      open: true,
                                      title: isRTL ? 'تأكيد الحذف' : 'Confirm Removal',
                                      message: isRTL
                                        ? `هل أنت متأكد من حذف "${member.name}" من الجروب؟`
                                        : `Are you sure you want to remove "${member.name}" from the group?`,
                                      variant: 'error',
                                      showCancel: true,
                                      onConfirm: () => handleRemoveMember(member.id),
                                    });
                                  }}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '700'
                                  }}
                                >
                                  {isRTL ? 'حذف' : 'Remove'}
                                </button>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: c.text }}>
                      {isRTL ? 'إضافة أعضاء جدد' : 'Add New Members'}
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {initState.allEmployees
                        .filter(emp =>
                          !isSameUser(emp.id, currentUser?.infinityCode) &&
                          !(selectedUser.members || []).some(m => isSameUser(m.id, emp.id)) &&
                          (emp.englishName || emp.name || '').toLowerCase().includes(editGroupSearch.toLowerCase())
                        )
                        .map(emp => {
                          const initials = emp.name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                          const avatarSrc = employeeProfilePics[emp.id];
                          return (
                            <div
                              key={emp.id}
                              className="employee-item-card"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 12px',
                                borderRadius: '14px'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                  width: '32px', height: '32px', borderRadius: '50%',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: avatarSrc ? 'transparent' : getAvatarColor(emp.name),
                                  color: '#fff', fontSize: '12px', fontWeight: 'bold'
                                }}>
                                  {avatarSrc ? (
                                    <img src={avatarSrc} alt={emp.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                  ) : <User size={16} />}
                                </div>
                                <span style={{ fontWeight: '600', fontSize: '13px', color: c.text }}>{emp.name}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleAddMember(emp.id)}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: '#2e7cba',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '700'
                                }}
                              >
                                {isRTL ? 'إضافة' : 'Add'}
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${c.border}` }}>
                  <button
                    type="button"
                    onClick={() => setShowEditGroupModal(false)}
                    className="btn-gradient"
                    style={{
                      padding: '10px 18px',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {isRTL ? 'إغلاق' : 'Close'}
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* Messenger Phase 1 — Group Settings modal (postingMode,
              membershipMode/sync source for canCreateOrgGroup users, and a
              sync-status readout for DEPARTMENT_SYNC groups). */}
          {showGroupSettingsModal && groupSettingsTarget && (
            <div
              onClick={closeGroupSettingsModal}
              style={{
                position: 'fixed', inset: 0, zIndex: 1000, background: theme === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', backdropFilter: 'blur(4px)'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', maxWidth: 460, background: theme === 'dark' ? '#1e293b' : '#ffffff', borderRadius: '24px',
                  boxShadow: c.shadow, padding: '24px', border: `1px solid ${c.border}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: c.text }}>
                    {t.groupSettings}
                  </h3>
                  <button type="button" onClick={closeGroupSettingsModal}
                    style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', fontSize: '20px' }}>
                    ×
                  </button>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{t.postingMode}</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                      { key: 'EVERYONE', label: t.postingModeEveryone },
                      { key: 'ADMINS_ONLY', label: t.postingModeAdminsOnly },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setSettingsPostingMode(opt.key)}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '12px',
                          border: `1px solid ${settingsPostingMode === opt.key ? '#2e7cba' : c.border}`,
                          background: settingsPostingMode === opt.key ? '#2e7cba' : 'transparent',
                          color: settingsPostingMode === opt.key ? '#fff' : c.text,
                          fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {canCreateOrgGroup && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{t.membershipMode}</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { key: 'MANUAL', label: t.membershipModeManual },
                        { key: 'DEPARTMENT_SYNC', label: t.membershipModeDepartmentSync },
                        { key: 'COMPANY_SYNC', label: t.membershipModeCompanySync },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setSettingsMembershipMode(opt.key)}
                          style={{
                            flex: 1, padding: '10px', borderRadius: '12px',
                            border: `1px solid ${settingsMembershipMode === opt.key ? '#2e7cba' : c.border}`,
                            background: settingsMembershipMode === opt.key ? '#2e7cba' : 'transparent',
                            color: settingsMembershipMode === opt.key ? '#fff' : c.text,
                            fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {settingsMembershipMode === 'DEPARTMENT_SYNC' && (
                      <div style={{ marginTop: '10px' }}>
                        <ReusableDropdown
                          items={settingsSyncDepartments}
                          value={settingsSyncDepartmentId ? String(settingsSyncDepartmentId) : ''}
                          onChange={(val) => setSettingsSyncDepartmentId(val ? Number(val) : null)}
                          onSelect={(dept) => setSettingsSyncDepartmentId(dept.id)}
                          placeholder={settingsSyncDepartmentsLoading ? t.loadingDepartments : t.selectDepartmentToSync}
                          disabled={settingsSyncDepartmentsLoading}
                          theme={theme}
                          language={language}
                        />
                      </div>
                    )}
                    {settingsMembershipMode === 'COMPANY_SYNC' && (
                      <div style={{ marginTop: '10px' }}>
                        <ReusableDropdown
                          items={settingsSyncCompanies}
                          value={settingsSyncWorkingCompany || ''}
                          onChange={(val) => setSettingsSyncWorkingCompany(val || null)}
                          onSelect={(companyOpt) => setSettingsSyncWorkingCompany(companyOpt.companyName)}
                          getItemKey={(companyOpt) => companyOpt.companyName}
                          getItemLabel={(companyOpt) => companyOpt.companyName}
                          placeholder={settingsSyncCompaniesLoading ? t.loadingCompanies : t.selectCompanyToSync}
                          disabled={settingsSyncCompaniesLoading}
                          theme={theme}
                          language={language}
                        />
                      </div>
                    )}
                    {(settingsMembershipMode === 'DEPARTMENT_SYNC' || settingsMembershipMode === 'COMPANY_SYNC') && (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={settingsAutoAddNewMembers}
                          onChange={(e) => setSettingsAutoAddNewMembers(e.target.checked)}
                          style={{ marginTop: '2px' }}
                        />
                        <span>
                          <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: c.text }}>{t.autoAddNewMembers}</span>
                          <span style={{ display: 'block', fontSize: '11.5px', color: c.textSecondary, marginTop: '2px' }}>{t.autoAddNewMembersHint}</span>
                        </span>
                      </label>
                    )}
                  </div>
                )}

                {(settingsMembershipMode === 'DEPARTMENT_SYNC' || settingsMembershipMode === 'COMPANY_SYNC') && (
                  <div style={{ marginBottom: '18px', padding: '12px 14px', borderRadius: '14px', background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1px solid ${c.border}` }}>
                    <div style={{ fontSize: '12.5px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{t.syncStatus}</div>
                    {settingsMembershipMode === 'COMPANY_SYNC' && groupSyncStatus?.syncWorkingCompany && (
                      <div style={{ fontSize: '11.5px', color: c.textSecondary, marginBottom: '6px' }}>
                        {t.syncedToCompany.replace('{company}', groupSyncStatus.syncWorkingCompany)}
                      </div>
                    )}
                    {groupSyncStatusLoading ? (
                      <div style={{ fontSize: '12px', color: c.textSecondary }}>{t.loadingSyncStatus}</div>
                    ) : groupSyncStatus ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {(() => {
                            const status = groupSyncStatus.lastSyncStatus;
                            const badge = status === 'success'
                              ? { bg: '#d1fae5', color: '#065f46', label: t.syncHealthy }
                              : status === 'failed'
                                ? { bg: '#fee2e2', color: '#991b1b', label: t.syncFailed }
                                : { bg: '#fef3c7', color: '#92400e', label: t.syncPending };
                            return (
                              <span
                                title={status === 'failed' ? (groupSyncStatus.lastSyncError || '') : undefined}
                                style={{ fontSize: '11.5px', fontWeight: '700', padding: '4px 10px', borderRadius: '999px', background: badge.bg, color: badge.color }}
                              >
                                {badge.label}
                              </span>
                            );
                          })()}
                          <span style={{ fontSize: '12px', color: c.textSecondary }}>
                            {t.memberCount}: {groupSyncStatus.memberCount ?? 0}
                          </span>
                        </div>
                        <div style={{ fontSize: '11.5px', color: c.textSecondary }}>
                          {t.lastSynced}: {groupSyncStatus.lastSyncAt ? new Date(groupSyncStatus.lastSyncAt).toLocaleString(isRTL ? 'ar-EG' : 'en-US') : t.syncNeverRun}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: c.textSecondary }}>{t.syncNeverRun}</div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                  <button type="button" onClick={closeGroupSettingsModal} style={{ padding: '12px 18px', borderRadius: '14px', border: `1px solid ${c.border}`, background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9', color: c.text, cursor: 'pointer', fontWeight: '600' }}>{t.cancel}</button>
                  <button type="button" disabled={savingGroupSettings} onClick={handleSaveGroupSettings} className="btn-gradient" style={{ padding: '12px 18px', borderRadius: '14px', border: 'none', cursor: savingGroupSettings ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: savingGroupSettings ? 0.7 : 1 }}>
                    {savingGroupSettings ? t.saving : t.saveSettings}
                  </button>
                </div>
              </div>
            </div>
          )}

          {initState.selectedUser ? (
            <>
              <ChatHeader
                selectedUser={initState.selectedUser}
                typingUsers={typingUsers}
                employeeProfilePics={employeeProfilePics}
                onBack={() => initState.setSelectedUser(null)}
                onInfoClick={() => setShowRightPanel((prev) => !prev)}
                isRTL={isRTL}
                c={c}
                styles={styles}
                getAvatarColor={getAvatarColor}
                t={t}
                isMobile={isMobile}
                isOnline={selectedUserOnline}
                activeMembersCount={activeMembersCount}
                displayName={selectedDisplayName}
                onToggleSearch={() => {
                  setChatSearchOpen((prev) => !prev);
                  setChatSearchQuery('');
                  setChatSearchIndex(0);
                }}
                onOpenMediaDocs={() => setShowMediaDocs(true)}
                onTogglePin={() => chatPrefs.togglePinned(selectedUser.id)}
                isPinned={chatPrefs.isPinned(selectedUser.id)}
                onToggleFavorite={() => chatPrefs.toggleFavorite(selectedUser.id)}
                isFavorite={chatPrefs.isFavorite(selectedUser.id)}
                onToggleBlock={canBlockUsers ? () => chatPrefs.toggleBlocked(selectedUser.id) : undefined}
                isBlocked={selectedIsBlocked}
              />

              {chatSearchOpen && (
                <ChatSearchBar
                  query={chatSearchQuery}
                  onQueryChange={(value) => { setChatSearchQuery(value); setChatSearchIndex(0); }}
                  matchCount={chatSearchMatches.length}
                  activeIndex={chatSearchIndex}
                  onNavigate={navigateChatSearch}
                  onClose={() => { setChatSearchOpen(false); setChatSearchQuery(''); }}
                  c={c}
                  t={t}
                  isRTL={isRTL}
                  theme={theme}
                />
              )}

              <MessagesContainer
                messages={messages}
                selectedUser={initState.selectedUser}
                hasMoreMessages={messagesState.hasMoreMessages}
                messagesOffset={messagesState.messagesOffset}
                loadingMoreFor={messagesState.loadingMoreFor}
                loadMoreMessages={messagesState.loadMoreMessages}
                isRTL={isRTL}
                c={c}
                employeeProfilePics={employeeProfilePics}
                getAvatarColor={getAvatarColor}
                styles={styles}
                messagesContainerRef={messagesState.messagesContainerRef}
                messagesEndRef={messagesState.messagesEndRef}
                userAtBottomRef={messagesState.userAtBottomRef}
                t={t}
                language={language}
                theme={theme}
                onDeleteMessage={canDeleteMessages ? handleDeleteWithConfirm : undefined}
                openMediaViewer={openMediaViewer}
                onReply={canReplyMessages ? (message) => setReplyingTo(message) : undefined}
                onForward={canForwardMessages ? (message) => setForwardMsg(message) : undefined}
                onToggleStar={canStarMessages ? handleToggleStar : undefined}
                isStarredFn={chatPrefs.isStarred}
                onReact={canReactMessages ? messagesState.toggleReaction : undefined}
                canViewDetails={canViewMessageDetails}
                currentUserCode={currentUser?.infinityCode}
                resolveReactorName={resolveReactorName}
                searchTerm={chatSearchOpen ? chatSearchQuery : ''}
                jumpToMessageId={jumpToMessageId}
                onJumpHandled={() => setJumpToMessageId(null)}
              />

              {selectedUser.isArchived ? (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: c.textSecondary,
                  background: c.inputBg,
                  borderTop: `1px solid ${c.border}`,
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {isRTL ? 'لقد غادرت هذه الشات' : 'You have left this chat'}
                </div>
              ) : selectedIsBlocked ? (
                <div style={{
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  color: c.textSecondary,
                  background: c.inputBg,
                  borderTop: `1px solid ${c.border}`,
                  fontSize: '14px',
                  fontWeight: '600',
                  flexWrap: 'wrap',
                  textAlign: 'center',
                }}>
                  <Ban size={16} color="#ef4444" />
                  {t.blockedBanner}
                  {canBlockUsers && (
                  <button
                    type="button"
                    onClick={() => chatPrefs.toggleBlocked(selectedUser.id)}
                    style={{
                      border: '1px solid #2e7cba', background: 'transparent', color: '#2e7cba',
                      borderRadius: '10px', padding: '6px 14px', cursor: 'pointer', fontWeight: '700', fontSize: '13px',
                    }}
                  >
                    {t.unblockAction}
                  </button>
                  )}
                </div>
              ) : canSendMessages && postingModeBlocksMe ? (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: c.textSecondary,
                  background: c.inputBg,
                  borderTop: `1px solid ${c.border}`,
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {t.adminsOnlyComposerNote}
                </div>
              ) : canSendMessages && (
                <ChatInput
                  messageInput={messageInput}
                  setMessageInput={setMessageInput}
                  handleSendMessage={messagesState.handleSendMessage}
                  handleTyping={messagesState.handleTyping}
                  handleKeyPress={messagesState.handleKeyPress}
                  handleFileSelect={mediaState.handleFileSelect}
                  isRecording={voiceState.isRecording}
                  audioBlob={voiceState.audioBlob}
                  recordingTime={voiceState.recordingTime}
                  startRecording={voiceState.startRecording}
                  stopRecording={voiceState.stopRecording}
                  sendRecording={sendRecording}
                  cancelRecording={voiceState.cancelRecording}
                  uploading={mediaState.uploading}
                  isConnected={socketState.isConnected || contextIsConnected}
                  fileInputRef={fileInputRef}
                  isMobile={isMobile}
                  styles={styles}
                  c={c}
                  t={t}
                  isRTL={isRTL}
                  theme={theme}
                  replyingTo={replyingTo}
                  onCancelReply={() => setReplyingTo(null)}
                  selectedUserName={selectedDisplayName}
                />
              )}
            </>
          ) : (
            <EmptyState c={c} styles={styles} t={t} />
          )}
        </div>

        {selectedUser && showRightPanel && (
          <div ref={rightPanelRef} style={styles.rightPanel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: c.text }}>{t.contactInfo}</div>
              <button
                type="button"
                onClick={() => setShowRightPanel(false)}
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 14,
                  border: `1px solid ${c.border}`,
                  background: c.inputBg,
                  color: c.textSecondary,
                  cursor: 'pointer',
                  boxShadow: c.softShadow,
                  transition: 'all 0.2s ease',
                }}
                aria-label="Close info panel"
              >
                <X size={18} />
              </button>
            </div>
            <div style={styles.rightCard}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ position: 'relative', width: 64, height: 64 }}>
                  {employeeProfilePics[selectedUser.id] ? (
                    <img
                      src={employeeProfilePics[selectedUser.id]}
                      alt={selectedUser.name}
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 18,
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: '800',
                        fontSize: '20px',
                        background: getAvatarColor(selectedUser.name),
                      }}
                    >
                      {String(selectedUser.id).startsWith('group-') ? <Users size={32} /> : selectedUser.avatar}
                    </div>
                  )}

                  {isGroupConv(selectedUser) && isCurrentUserAdminOfSelectedGroup && (
                    <>
                      <button
                        title={isRTL ? 'تغيير الصورة' : 'Change Photo'}
                        onClick={() => groupPhotoInputRef.current?.click()}
                        style={{
                          position: 'absolute', bottom: -5, right: -5, width: 28, height: 28,
                          borderRadius: '50%', background: '#2e7cba', border: `2px solid ${c.sidebarBg}`,
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', zIndex: 2, padding: 0
                        }}
                      >
                        <Camera size={14} />
                      </button>
                      <input 
                        type="file" 
                        accept="image/*" 
                        ref={groupPhotoInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleGroupPhotoUpload}
                      />
                    </>
                  )}
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                  {aliasEditing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="text"
                        value={aliasValue}
                        autoFocus
                        onChange={(e) => setAliasValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            chatPrefs.setAlias(selectedUser.id, aliasValue);
                            setAliasEditing(false);
                          }
                          if (e.key === 'Escape') setAliasEditing(false);
                        }}
                        placeholder={selectedUser.name}
                        style={{
                          flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: '10px',
                          border: `1px solid #2e7cba`, background: c.inputBg, color: c.text,
                          outline: 'none', fontSize: '14px', fontWeight: '700',
                        }}
                      />
                      <button
                        type="button"
                        title={t.saveName}
                        aria-label={t.saveName}
                        onClick={() => {
                          chatPrefs.setAlias(selectedUser.id, aliasValue);
                          setAliasEditing(false);
                        }}
                        style={{
                          width: '30px', height: '30px', borderRadius: '10px', border: 'none',
                          background: '#2e7cba', color: '#fff', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        <Check size={15} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '17px',
                          fontWeight: '800',
                          color: c.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedDisplayName}
                      </div>
                      <button
                        type="button"
                        title={t.editName}
                        aria-label={t.editName}
                        onClick={() => {
                          setAliasValue(chatPrefs.getAlias(selectedUser.id) || '');
                          setAliasEditing(true);
                        }}
                        style={{
                          border: 'none', background: 'transparent', color: c.textSecondary,
                          cursor: 'pointer', display: 'flex', padding: '3px', flexShrink: 0,
                        }}
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  )}
                  {chatPrefs.getAlias(selectedUser.id) && !aliasEditing && (
                    <div style={{ fontSize: '12px', color: c.textSecondary, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedUser.name}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: '13px',
                      color: c.textSecondary,
                      marginTop: '4px',
                    }}
                  >
                    {selectedUserOnline ? t.activeNow : t.offline}
                  </div>
                  {(selectedUser.company || selectedUser.department) && (
                    <div
                      style={{
                        fontSize: '13px',
                        color: c.textSecondary,
                        marginTop: '6px',
                      }}
                    >
                      {selectedUser.company}
                      {selectedUser.company && selectedUser.department ? ' · ' : ''}
                      {selectedUser.department}
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.statGrid}>
                <div style={styles.statItem}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: c.text }}>
                    {imagesCount}
                  </div>
                  <div style={{ fontSize: '11px', color: c.textSecondary }}>Images</div>
                </div>

                <div style={styles.statItem}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: c.text }}>
                    {docsCount}
                  </div>
                  <div style={{ fontSize: '11px', color: c.textSecondary }}>Docs</div>
                </div>

                <div style={styles.statItem}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: c.text }}>
                    {voiceCount}
                  </div>
                  <div style={{ fontSize: '11px', color: c.textSecondary }}>Voice</div>
                </div>
              </div>
            </div>

            {/* Media, links and docs — expands into the tabbed page */}
            <div style={styles.rightCard}>
              <button
                type="button"
                onClick={() => setShowMediaDocs(true)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '8px', border: 'none', background: 'transparent', cursor: 'pointer',
                  color: c.text, padding: 0, marginBottom: sharedMedia.length ? '10px' : 0,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '800' }}>
                  <FolderClosed size={16} color="#2e7cba" />
                  {t.mediaLinksDocs}
                  <span style={{ fontSize: '12px', fontWeight: '600', color: c.textSecondary }}>
                    ({imagesCount + docsCount})
                  </span>
                </span>
                {isRTL ? <ChevronLeft size={16} color={c.textSecondary} /> : <ChevronRight size={16} color={c.textSecondary} />}
              </button>
              {sharedMedia.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                  {sharedMedia.map((msg) => (
                    <div
                      key={msg.id}
                      onClick={() => openMediaViewer(msg)}
                      style={{
                        aspectRatio: '1', borderRadius: '10px', overflow: 'hidden',
                        cursor: 'pointer', border: `1px solid ${c.border}`,
                      }}
                    >
                      <img
                        src={msg.mediaUrl || msg.thumbnailUrl}
                        alt={msg.mediaName || 'media'}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Common groups + contact controls (1-to-1 chats only) */}
            {!isGroupConv(selectedUser) && (
              <div style={styles.rightCard}>
                <div style={{ fontSize: '14px', fontWeight: '800', color: c.text, marginBottom: '10px' }}>
                  {t.commonGroups} ({commonGroups.length})
                </div>
                {commonGroups.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                    {commonGroups.map((group) => (
                      <div
                        key={group.id}
                        onClick={() => handleSelectUser(group)}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                      >
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: getAvatarColor(group.name), color: '#fff',
                        }}>
                          <Users size={16} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {group.name}
                          </div>
                          <div style={{ display: 'flex', marginTop: '3px' }}>
                            {(group.members || []).slice(0, 4).map((member, mi) => {
                              const pic = employeeProfilePics[member.id];
                              return (
                                <div
                                  key={member.id}
                                  title={member.name}
                                  style={{
                                    width: '18px', height: '18px', borderRadius: '50%',
                                    border: `1.5px solid ${c.sidebarBg}`, overflow: 'hidden',
                                    marginInlineStart: mi === 0 ? 0 : '-6px',
                                    background: pic ? 'transparent' : getAvatarColor(member.name),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontSize: '8px', fontWeight: '700', flexShrink: 0,
                                  }}
                                >
                                  {pic
                                    ? <img src={pic} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : (member.name || '?').trim().charAt(0).toUpperCase()}
                                </div>
                              );
                            })}
                            {(group.members || []).length > 4 && (
                              <span style={{ fontSize: '10px', color: c.textSecondary, marginInlineStart: '4px' }}>
                                +{group.members.length - 4}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Controls: mute / favorite / add-to-list / block */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: `1px solid ${c.border}`, paddingTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => chatPrefs.toggleMuted(selectedUser.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: c.text, padding: '8px 2px', fontSize: '13.5px', fontWeight: '600',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <BellOff size={16} color={c.textSecondary} />
                      {t.muteNotifications}
                    </span>
                    <span
                      style={{
                        width: '36px', height: '20px', borderRadius: '999px', position: 'relative',
                        background: chatPrefs.isMuted(selectedUser.id) ? '#2e7cba' : (theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#cbd5e1'),
                        transition: 'background 0.2s ease', flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute', top: '2px',
                          insetInlineStart: chatPrefs.isMuted(selectedUser.id) ? '18px' : '2px',
                          width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                          transition: 'inset-inline-start 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                        }}
                      />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => chatPrefs.toggleFavorite(selectedUser.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: c.text, padding: '8px 2px', fontSize: '13.5px', fontWeight: '600', textAlign: 'start',
                    }}
                  >
                    <Heart
                      size={16}
                      fill={chatPrefs.isFavorite(selectedUser.id) ? '#ef4444' : 'none'}
                      color={chatPrefs.isFavorite(selectedUser.id) ? '#ef4444' : c.textSecondary}
                    />
                    {chatPrefs.isFavorite(selectedUser.id) ? t.removeFromFavorites : t.addToFavorites}
                  </button>

                 {/* <button
                    type="button"
                    onClick={() => { setListInputOpen((p) => !p); setListNameValue(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: c.text, padding: '8px 2px', fontSize: '13.5px', fontWeight: '600', textAlign: 'start',
                    }}
                  >
                    <ListPlus size={16} color={c.textSecondary} />
                    {t.addToList}
                  </button>*/}
                  {listInputOpen && (
                    <div style={{ display: 'flex', gap: '6px', padding: '0 2px 6px' }}>
                      <input
                        type="text"
                        value={listNameValue}
                        autoFocus
                        onChange={(e) => setListNameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && listNameValue.trim()) {
                            chatPrefs.addToCustomList(listNameValue.trim(), selectedUser.id);
                            setListInputOpen(false);
                          }
                          if (e.key === 'Escape') setListInputOpen(false);
                        }}
                        placeholder={t.listNamePrompt}
                        style={{
                          flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: '10px',
                          border: `1px solid ${c.border}`, background: c.inputBg, color: c.text,
                          outline: 'none', fontSize: '13px',
                        }}
                      />
                      <button
                        type="button"
                        disabled={!listNameValue.trim()}
                        onClick={() => {
                          chatPrefs.addToCustomList(listNameValue.trim(), selectedUser.id);
                          setListInputOpen(false);
                        }}
                        style={{
                          width: '30px', height: '30px', borderRadius: '10px', border: 'none',
                          background: '#2e7cba', color: '#fff', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', cursor: 'pointer', opacity: listNameValue.trim() ? 1 : 0.5,
                        }}
                      >
                        <Check size={15} />
                      </button>
                    </div>
                  )}

                  {canBlockUsers && (
                  <button
                    type="button"
                    onClick={() => chatPrefs.toggleBlocked(selectedUser.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: selectedIsBlocked ? '#2e7cba' : '#ef4444',
                      padding: '8px 2px', fontSize: '13.5px', fontWeight: '700', textAlign: 'start',
                    }}
                  >
                    <Ban size={16} />
                    {selectedIsBlocked ? t.unblockAction : t.blockAction}
                  </button>
                  )}
                </div>
              </div>
            )}


            <div style={styles.rightCard}>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '800',
                  color: c.text,
                  marginBottom: '10px',
                }}
              >
                {isGroupConv(selectedUser) ? (isRTL ? 'معلومات الجروب' : 'Group Info') : t.aboutChat}
              </div>

              {isGroupConv(selectedUser) ? (
                <div style={{ fontSize: '13px', color: c.textSecondary }}>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ fontWeight: '600', color: c.text }}>
                        {isRTL ? 'الأعضاء' : 'Members'} ({selectedUser.members?.length || 0})
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {!selectedUser.isArchived && isCurrentUserAdminOfSelectedGroup && (
                          <button
                            onClick={() => openGroupSettingsModal(selectedUser)}
                            title={t.groupSettings}
                            style={{
                              background: theme === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
                              border: 'none',
                              color: c.textSecondary,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '30px',
                              height: '30px',
                              borderRadius: '8px',
                              transition: 'background 0.2s',
                            }}
                          >
                            <Settings size={15} />
                          </button>
                        )}
                        {!selectedUser.isArchived && isCurrentUserAdminOfSelectedGroup && (
                          <button
                            onClick={() => {
                              setEditGroupSearch('');
                              setShowEditGroupModal(true);
                            }}
                            title={isRTL ? 'إضافة / تعديل' : 'Add / Edit Members'}
                            style={{
                              background: theme === 'dark' ? 'rgba(46,124,186,0.15)' : 'rgba(46,124,186,0.1)',
                              border: 'none',
                              color: '#2e7cba',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '30px',
                              height: '30px',
                              borderRadius: '8px',
                              transition: 'background 0.2s',
                            }}
                          >
                            <UserPlus size={15} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '200px', overflowY: 'auto', paddingRight: '4px' }}>
                      {(() => {
                        const isCurrentUserAdmin = isCurrentUserAdminOfSelectedGroup;
                        const sortedMembers = [...(selectedUser.members || [])].sort((a, b) => {
                          if (a.role === 'admin' && b.role !== 'admin') return -1;
                          if (b.role === 'admin' && a.role !== 'admin') return 1;
                          return 0;
                        });
                        return sortedMembers.map(m => {
                          const isMe = isSameUser(m.id, currentUser?.infinityCode);
                          const isAdmin = m.role === 'admin';
                          const avatarSrc = employeeProfilePics[m.id];
                          const initials = m.name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

                          return (
                            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{
                                width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: avatarSrc ? 'transparent' : getAvatarColor(m.name),
                                color: '#fff', fontSize: '12px', fontWeight: 'bold',
                                border: isAdmin ? '2px solid #2e7cba' : 'none',
                                padding: isAdmin && avatarSrc ? '2px' : '0',
                                boxSizing: 'border-box'
                              }}>
                                {avatarSrc ? (
                                  <img src={avatarSrc} alt={m.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                ) : <User size={16} />}
                              </div>
                              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                <span style={{ color: c.text, fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {m.name} {isMe && (isRTL ? '(أنت)' : '(You)')}
                                </span>
                                <span style={{ fontSize: '11px', color: isAdmin ? '#2e7cba' : c.textSecondary }}>
                                  {isAdmin ? (isRTL ? 'أدمن' : 'Admin') : (isRTL ? 'عضو' : 'Member')}
                                </span>
                              </div>

                              {isCurrentUserAdmin && !isMe && !selectedUser.isArchived && (
                                <button
                                  title={isAdmin ? (isRTL ? 'إلغاء الأدمن' : 'Remove Admin') : (isRTL ? 'ترقية لأدمن' : 'Make Admin')}
                                  onClick={() => {
                                    setAlertState({
                                      open: true,
                                      title: isAdmin ? (isRTL ? 'إلغاء الأدمن' : 'Remove Admin') : (isRTL ? 'ترقية لأدمن' : 'Make Admin'),
                                      message: isAdmin
                                        ? (isRTL ? `هل أنت متأكد من إلغاء دور الأدمن عن ${m.name}؟` : `Are you sure you want to remove admin role from ${m.name}?`)
                                        : (isRTL ? `هل أنت متأكد من ترقية ${m.name} ليكون أدمن؟` : `Are you sure you want to make ${m.name} an admin?`),
                                      variant: 'warning',
                                      showCancel: true,
                                      onConfirm: () => {
                                        handleSetAdminRole(m.id, !isAdmin);
                                        setAlertState(prev => ({ ...prev, open: false }));
                                      }
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: isAdmin ? c.textSecondary : '#2e7cba',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseOver={(e) => { e.currentTarget.style.background = c.inputBg; }}
                                  onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <Shield size={16} fill={isAdmin ? '#2e7cba' : 'none'} color={isAdmin ? '#2e7cba' : c.textSecondary} />
                                </button>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                  {!selectedUser.isArchived && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <button
                        onClick={() => {
                          setAlertState({
                            open: true,
                            title: isRTL ? 'مغادرة الجروب' : 'Leave Group',
                            message: isRTL ? 'هل أنت متأكد أنك تريد مغادرة الجروب؟' : 'Are you sure you want to leave this group?',
                            variant: 'error',
                            showCancel: true,
                            onConfirm: async () => {
                              try {
                                const actualGroupId = selectedUser.id.replace('group-', '');
                                const response = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/members/${currentUser.infinityCode}`, { method: 'DELETE' });
                                if (response.ok) {
                                  setConversations(prev => prev.map(c =>
                                    c.id === selectedUser.id ? { ...c, isArchived: true } : c
                                  ));
                                  initState.setSelectedUser(prev => ({ ...prev, isArchived: true }));
                                  setAlertState(prev => ({ ...prev, open: false }));
                                } else {
                                  setAlertState({ open: true, title: t.error, message: isRTL ? 'فشل في مغادرة الجروب' : 'Failed to leave group', variant: 'error' });
                                }
                              } catch (e) {
                                setAlertState({ open: true, title: t.error, message: isRTL ? 'حدث خطأ أثناء المغادرة' : 'Error leaving group', variant: 'error' });
                              }
                            }
                          });
                        }}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ef4444',
                          color: '#ef4444', background: 'transparent', cursor: 'pointer', fontWeight: '600', fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {isRTL ? 'مغادرة الجروب' : 'Leave Group'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: c.textSecondary, lineHeight: 1.8 }}>
                  {selectedUser.company && (
                    <div>
                      <strong style={{ color: c.text }}>Company:</strong> {selectedUser.company}
                    </div>
                  )}
                  {selectedUser.department && (
                    <div>
                      <strong style={{ color: c.text }}>Department:</strong> {selectedUser.department}
                    </div>
                  )}
                  {selectedUser.position && (
                    <div>
                      <strong style={{ color: c.text }}>Position:</strong> {selectedUser.position}
                    </div>
                  )}
                  {selectedUser.email && (
                    <div>
                      <strong style={{ color: c.text }}>Email:</strong> {selectedUser.email}
                    </div>
                  )}
                  {!selectedUser.company && !selectedUser.department && !selectedUser.position && !selectedUser.email && (
                    <div>
                      Conversation details and shared content can appear here in a clean professional side panel.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <style>{`
          @media (min-width: 768px) {
            .md-sidebar {
              display: flex !important;
              width: 380px !important;
              min-width: 380px;
            }
            .md-chat {
              display: flex !important;
            }
            .mobile-back {
              display: none !important;
            }
          }
          @media (max-width: 767px) {
            .hide-mobile {
              display: none !important;
            }
          }

          button:hover:not(:disabled) {
            background: ${c.hoverBg} !important;
            transform: scale(1.08);
          }

          button.no-hover-effect:hover:not(:disabled) {
            background: linear-gradient(135deg, #2e7cba, #2e7cba) !important;
            transform: none !important;
          }

          button.no-hover-effect:active:not(:disabled) {
            transform: none !important;
          }

          .btn-gradient {
            background: linear-gradient(135deg, #2e7cba, #1a6baa) !important;
            color: #ffffff !important;
            transition: all 0.2s ease !important;
            box-shadow: 0 4px 12px rgba(46, 124, 186, 0.15) !important;
          }
          .btn-gradient:hover:not(:disabled) {
            background: linear-gradient(135deg, #378cd3, #237bbd) !important;
            transform: scale(1.05) !important;
            box-shadow: 0 4px 12px rgba(46, 124, 186, 0.35) !important;
          }
          .btn-gradient:active:not(:disabled) {
            transform: scale(0.95) !important;
          }

          .employee-item-card {
            background: ${theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc'} !important;
            border: 1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} !important;
            transition: all 0.2s ease !important;
          }
          .employee-item-card:hover {
            background: ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'} !important;
            border-color: #2e7cba !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
          }
          .employee-item-card.selected {
            background: ${theme === 'dark' ? 'rgba(46, 124, 186, 0.15)' : '#f0f7ff'} !important;
            border-color: #2e7cba !important;
          }

          button:active:not(:disabled) {
            transform: scale(0.95);
          }

          input:focus, textarea:focus {
            border-color: #2e7cba !important;
          }

          * {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          *::-webkit-scrollbar {
            display: none;
          }

          * {
            transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
          }

          textarea {
            resize: none;
            font-family: inherit;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          @keyframes recWave {
            0%, 100% { transform: scaleY(0.3); }
            50% { transform: scaleY(1); }
          }

          /* Keep the inline background of the voice-recorder round buttons on
             hover — the generic button:hover rule above would wash it out. */
          .voice-btn:hover:not(:disabled) {
            background: var(--vb-bg) !important;
            filter: brightness(1.1);
            transform: scale(1.06);
          }

          @keyframes messageSlideIn {
            from {
              opacity: 0;
              transform: translateY(12px) scale(0.97);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.92);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes popoverSlideUp {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .msg-info-btn {
            opacity: 0;
            transition: opacity 0.2s ease, color 0.15s ease !important;
          }

          .message-wrapper:hover .msg-info-btn {
            opacity: 0.7;
          }

          .msg-info-btn:hover {
            opacity: 1 !important;
            color: ${c.text} !important;
            background: transparent !important;
            transform: none !important;
          }

          .message-bubble-wrapper {
            transition: box-shadow 0.2s ease, transform 0.15s ease !important;
          }

          .message-bubble-wrapper:hover {
            transform: none !important;
            box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12) !important;
          }

          @media (max-width: 767px) {
            .msg-info-btn {
              opacity: 0.6;
            }
          }
        `}</style>
      </div>

      {/* ── New Chat modal — lives at fragment root so it's always rendered
           regardless of whether chatArea has display:none on mobile ── */}
      {showNewChatModal && (
        <div
          onClick={() => setShowNewChatModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000, background: theme === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            // Mobile Chrome/Safari sometimes fail to paint a freshly-mounted
            // fixed + backdrop-filter layer until the next unrelated repaint
            // (e.g. tapping a conversation) — force it onto its own
            // compositing layer immediately so it shows up on first tap.
            transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 680, background: theme === 'dark' ? '#1e293b' : '#ffffff', borderRadius: '24px',
              boxShadow: c.shadow, padding: '24px', overflowY: 'hidden', height: '95vh',
              border: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: c.text }}>
                {isRTL ? 'محادثة جديدة' : 'New Chat'}
              </h3>
              <button type="button" onClick={() => setShowNewChatModal(false)}
                style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', fontSize: '20px' }}>
                ×
              </button>
            </div>

            {canCreateGroup && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: theme === 'dark' ? 'rgba(51, 65, 85, 0.5)' : '#f1f5f9', padding: '4px', borderRadius: '14px' }}>
              <button onClick={() => setNewChatModalTab('direct')} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: newChatModalTab === 'direct' ? (theme === 'dark' ? 'rgba(46, 124, 186, 0.3)' : '#ffffff') : 'transparent', color: newChatModalTab === 'direct' ? (theme === 'dark' ? '#ffffff' : '#2e7cba') : c.textSecondary, fontWeight: '700', cursor: 'pointer', boxShadow: newChatModalTab === 'direct' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none' }}>
                {isRTL ? 'رسالة مباشرة' : 'Direct Message'}
              </button>
              <button onClick={() => setNewChatModalTab('group')} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: newChatModalTab === 'group' ? (theme === 'dark' ? 'rgba(46, 124, 186, 0.3)' : '#ffffff') : 'transparent', color: newChatModalTab === 'group' ? (theme === 'dark' ? '#ffffff' : '#2e7cba') : c.textSecondary, fontWeight: '700', cursor: 'pointer', boxShadow: newChatModalTab === 'group' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none' }}>
                {isRTL ? 'جروب جديد' : 'New Group'}
              </button>
            </div>
            )}

            {newChatModalTab === 'direct' || !canCreateGroup ? (
              <div key="direct-chat-tab" style={{ display: 'contents' }}>
                <div style={{ marginBottom: '16px', position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: isRTL ? 'auto' : '14px', right: isRTL ? '14px' : 'auto', top: '50%', transform: 'translateY(-50%)', color: c.textSecondary }} />
                  <input type="text" value={newChatSearch} onChange={(e) => setNewChatSearch(e.target.value)} placeholder={t.searchPeople} style={{ width: '100%', padding: `12px ${isRTL ? '40px' : '14px'} 12px ${isRTL ? '14px' : '40px'}`, borderRadius: '14px', border: `1px solid ${c.border}`, background: c.inputBg, color: c.text, outline: 'none' }} />
                </div>
                <div style={{
                  flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '4px', paddingTop: '4px',
                }}>
                  {directChatCandidates.items.map(emp => (
                      <div key={emp.id} onClick={() => { initState.startNewConversation(emp); setShowNewChatModal(false); }} className="employee-item-card" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '14px', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: getAvatarColor(emp.name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                          <User size={20} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: c.text, fontWeight: '600', fontSize: '14px' }}>{emp.name}</div>
                        </div>
                      </div>
                    ))}
                  {directChatCandidates.total > MAX_RENDERED_RESULTS && (
                    <div style={{ padding: '10px 12px', fontSize: '12px', color: c.textSecondary, textAlign: 'center' }}>
                      {isRTL
                        ? `${directChatCandidates.total - MAX_RENDERED_RESULTS} نتيجة أخرى — اكتب المزيد لتضييق البحث`
                        : `${directChatCandidates.total - MAX_RENDERED_RESULTS} more — keep typing to narrow it down`}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div key="new-group-tab" style={{ display: 'contents' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{t.groupName}</label>
                  <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder={t.groupName} style={{ width: '100%', padding: '12px 14px', borderRadius: '14px', border: `1px solid ${c.border}`, background: c.inputBg, color: c.text, outline: 'none' }} />
                </div>

                {/* Messenger Phase 1: posting mode — who's allowed to send. */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{t.postingMode}</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                      { key: 'EVERYONE', label: t.postingModeEveryone },
                      { key: 'ADMINS_ONLY', label: t.postingModeAdminsOnly },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setGroupPostingMode(opt.key)}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '12px',
                          border: `1px solid ${groupPostingMode === opt.key ? '#2e7cba' : c.border}`,
                          background: groupPostingMode === opt.key ? '#2e7cba' : 'transparent',
                          color: groupPostingMode === opt.key ? '#fff' : c.text,
                          fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Messenger Phase 1: membership mode — Department Sync is gated
                    behind the stronger createOrgGroup permission (mirrors the
                    server-side gate in chatGroupRoutes.js). */}
                {canCreateOrgGroup && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{t.membershipMode}</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { key: 'MANUAL', label: t.membershipModeManual },
                        { key: 'DEPARTMENT_SYNC', label: t.membershipModeDepartmentSync },
                        { key: 'COMPANY_SYNC', label: t.membershipModeCompanySync },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setGroupMembershipMode(opt.key)}
                          style={{
                            flex: 1, padding: '10px', borderRadius: '12px',
                            border: `1px solid ${groupMembershipMode === opt.key ? '#2e7cba' : c.border}`,
                            background: groupMembershipMode === opt.key ? '#2e7cba' : 'transparent',
                            color: groupMembershipMode === opt.key ? '#fff' : c.text,
                            fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {groupMembershipMode === 'DEPARTMENT_SYNC' && (
                      <div style={{ marginTop: '10px' }}>
                        <ReusableDropdown
                          items={syncDepartments}
                          value={groupSyncDepartmentId ? String(groupSyncDepartmentId) : ''}
                          onChange={(val) => setGroupSyncDepartmentId(val ? Number(val) : null)}
                          onSelect={(dept) => setGroupSyncDepartmentId(dept.id)}
                          placeholder={syncDepartmentsLoading ? t.loadingDepartments : t.selectDepartmentToSync}
                          disabled={syncDepartmentsLoading}
                          theme={theme}
                          language={language}
                        />
                      </div>
                    )}
                    {groupMembershipMode === 'COMPANY_SYNC' && (
                      <div style={{ marginTop: '10px' }}>
                        <ReusableDropdown
                          items={syncCompanies}
                          value={groupSyncWorkingCompany || ''}
                          onChange={(val) => setGroupSyncWorkingCompany(val || null)}
                          onSelect={(companyOpt) => setGroupSyncWorkingCompany(companyOpt.companyName)}
                          getItemKey={(companyOpt) => companyOpt.companyName}
                          getItemLabel={(companyOpt) => companyOpt.companyName}
                          placeholder={syncCompaniesLoading ? t.loadingCompanies : t.selectCompanyToSync}
                          disabled={syncCompaniesLoading}
                          theme={theme}
                          language={language}
                        />
                      </div>
                    )}
                    {(groupMembershipMode === 'DEPARTMENT_SYNC' || groupMembershipMode === 'COMPANY_SYNC') && (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={groupAutoAddNewMembers}
                          onChange={(e) => setGroupAutoAddNewMembers(e.target.checked)}
                          style={{ marginTop: '2px' }}
                        />
                        <span>
                          <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: c.text }}>{t.autoAddNewMembers}</span>
                          <span style={{ display: 'block', fontSize: '11.5px', color: c.textSecondary, marginTop: '2px' }}>{t.autoAddNewMembersHint}</span>
                        </span>
                      </label>
                    )}
                  </div>
                )}

                {(groupMembershipMode === 'DEPARTMENT_SYNC' || groupMembershipMode === 'COMPANY_SYNC') && (
                  <div style={{ marginBottom: '16px', fontSize: '12px', color: c.textSecondary }}>
                    {t.syncGroupMembersHint}
                  </div>
                )}

                {groupMembershipMode === 'MANUAL' && (
                  <div style={{ marginBottom: '16px', position: 'relative' }}>
                    <Search size={18} style={{ position: 'absolute', left: isRTL ? 'auto' : '14px', right: isRTL ? '14px' : 'auto', top: '50%', transform: 'translateY(-50%)', color: c.textSecondary }} />
                    <input
                      type="text"
                      value={newGroupSearch}
                      onChange={(e) => setNewGroupSearch(e.target.value)}
                      placeholder={t.searchPeople || "Search..."}
                      style={{
                        width: '100%',
                        padding: `12px ${isRTL ? '40px' : '14px'} 12px ${isRTL ? '14px' : '40px'}`,
                        borderRadius: '14px',
                        border: `1px solid ${c.border}`,
                        background: c.inputBg,
                        color: c.text,
                        outline: 'none'
                      }}
                    />
                  </div>
                )}

                <div style={{ marginBottom: '18px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: c.text, marginBottom: '10px' }}>{t.selectGroupMembers}</div>
                  <div style={{
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    paddingRight: '4px',
                    paddingTop: '4px',
                  }}>
                    {groupMemberCandidates.items.map((emp) => {
                        const selected = groupMembers.some((member) => isSameUser(member.id, emp.id));
                        return (
                          <div key={emp.id} onClick={() => toggleGroupMember(emp)} className={`employee-item-card ${selected ? 'selected' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 14px', borderRadius: '14px', color: c.text, cursor: 'pointer', textAlign: 'left' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{
                                width: '32px', height: '32px',
                                borderRadius: '50%',
                                background: getAvatarColor(emp.name),
                                color: '#fff', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontSize: '12px', fontWeight: 'bold'
                              }}><User size={16} /></div>
                              <span style={{ fontWeight: '600', fontSize: '14px' }}>{emp.name}</span>
                            </div>
                            <span style={{ fontSize: '12px', color: selected ? '#2e7cba' : c.textSecondary, fontWeight: '700' }}>{selected ? t.remove : t.add}</span>
                          </div>
                        );
                      })}
                    {groupMemberCandidates.total > MAX_RENDERED_RESULTS && (
                      <div style={{ padding: '10px 12px', fontSize: '12px', color: c.textSecondary, textAlign: 'center' }}>
                        {isRTL
                          ? `${groupMemberCandidates.total - MAX_RENDERED_RESULTS} نتيجة أخرى — اكتب المزيد لتضييق البحث`
                          : `${groupMemberCandidates.total - MAX_RENDERED_RESULTS} more — keep typing to narrow it down`}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${c.border}` }}>
                  <button type="button" onClick={() => { setShowNewChatModal(false); resetGroupCreationState(); }} style={{ padding: '12px 18px', borderRadius: '14px', border: `1px solid ${c.border}`, background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9', color: c.text, cursor: 'pointer', fontWeight: '600' }}>{t.cancel}</button>
                  <button type="button" onClick={handleCreateGroup} className="btn-gradient" style={{ padding: '12px 18px', borderRadius: '14px', border: 'none', cursor: 'pointer', fontWeight: '600' }}>{t.createGroup}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Starred messages page (opened from the sidebar 3-dots menu) ── */}
      {showStarredModal && (
        <StarredMessagesModal
          starredMessages={chatPrefs.prefs.starredMessages}
          onClose={() => setShowStarredModal(false)}
          onOpenChat={handleOpenStarredChat}
          onUnstar={(snapshot) => chatPrefs.toggleStarMessage(snapshot)}
          c={c}
          t={t}
          isRTL={isRTL}
          theme={theme}
        />
      )}

      {/* ── Forward picker ── */}
      {forwardMsg && (
        <ForwardModal
          conversations={conversations}
          onClose={() => setForwardMsg(null)}
          onForward={handleForwardConfirm}
          c={c}
          t={t}
          isRTL={isRTL}
          theme={theme}
          getAvatarColor={getAvatarColor}
          employeeProfilePics={employeeProfilePics}
        />
      )}

      {/* ── Media / links / docs tabbed page ── */}
      {showMediaDocs && selectedUser && (
        <MediaLinksDocsModal
          messages={currentChatMessages}
          chatName={selectedDisplayName}
          onClose={() => setShowMediaDocs(false)}
          onOpenImage={(message) => { setShowMediaDocs(false); openMediaViewer(message); }}
          onOpenDoc={(message) => { setShowMediaDocs(false); openMediaViewer(message); }}
          c={c}
          t={t}
          isRTL={isRTL}
          theme={theme}
        />
      )}

      {/* ── Unified lightbox / file viewer (chat bubbles + side panel + docs) ── */}
      {viewerState?.message && (
        <MediaViewerModal
          mediaUrl={viewerState.message.mediaUrl}
          mediaType={viewerState.message.type}
          mediaName={viewerState.message.mediaName}
          onClose={() => setViewerState(null)}
          isRTL={isRTL}
          t={t}
          images={viewerImageIndex >= 0 ? chatImages : null}
          currentIndex={viewerImageIndex}
          onNavigate={(newIndex) => setViewerState({ message: chatImages[newIndex] })}
          message={viewerState.message}
          onReply={canReplyMessages ? (message) => setReplyingTo(message) : null}
          onForward={canForwardMessages ? (message) => setForwardMsg(message) : null}
          onToggleStar={canStarMessages ? handleToggleStar : null}
          isStarred={chatPrefs.isStarred(viewerState.message.id)}
          onDelete={canDeleteMessages ? handleDeleteWithConfirm : null}
          onReact={canReactMessages ? messagesState.toggleReaction : null}
        />
      )}

      <AlertModal
        open={alertState.open}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        showCancel={alertState.showCancel}
        onConfirm={() => {
          if (alertState.onConfirm) {
            alertState.onConfirm();
          } else {
            setAlertState(prev => ({ ...prev, open: false }));
          }
        }}
        onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
        theme={theme}
        language={language}
      />
    </>
  );
};

export default MessengerPage;